import React, { useCallback, useEffect, useRef, useState } from 'react';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import Toast from 'react-bootstrap/Toast';
import ToastContainer from 'react-bootstrap/ToastContainer';
import NavBar from './components/NavBar';
import './style.css';
import Login from './components/Login';
import StudentMarketingLanding from './components/StudentMarketingLanding';
import FacilitatorHome from './components/dashboard/FacilitatorHome';
import FacilitatorCaseAuthoringWorkspace from './components/dashboard/FacilitatorCaseAuthoringWorkspace';
import FacilitatorCaseLibrary from './components/dashboard/FacilitatorCaseLibrary';
import FacilitatorAssignments from './components/dashboard/FacilitatorAssignments';
import FacilitatorAssignmentResults from './components/dashboard/FacilitatorAssignmentResults';
import StudentMcqCatalog from './components/dashboard/StudentMcqCatalog';
import FacilitatorMcqEditor from './components/dashboard/FacilitatorMcqEditor';
import FacilitatorQuestionBankItemLibrary from './components/dashboard/FacilitatorQuestionBankItemLibrary';
import FacilitatorQuestionBankItemEditor from './components/dashboard/FacilitatorQuestionBankItemEditor';
import DrillSessionPlayer from './components/player/DrillSessionPlayer';
import FacilitatorCaseResults from './components/dashboard/FacilitatorCaseResults';
import FacilitatorAttemptReview from './components/dashboard/FacilitatorAttemptReview';
import FacilitatorAdminWorkspace from './components/dashboard/FacilitatorAdminWorkspace';
import PlatformAdminWorkspace from './components/dashboard/PlatformAdminWorkspace';
import FacilitatorCasePresentation from './components/dashboard/FacilitatorCasePresentation';
import StudentDashboard from './components/dashboard/StudentDashboard';
import StudentHome from './components/dashboard/StudentHome';
import StudentEpmaWorkspace from './components/dashboard/StudentEpmaWorkspace';
import StudentSpotErrorWorkspace from './components/dashboard/StudentSpotErrorWorkspace';
import FacilitatorSpotErrorLibrary from './components/dashboard/FacilitatorSpotErrorLibrary';
import FacilitatorSpotErrorEditor from './components/dashboard/FacilitatorSpotErrorEditor';
import FacilitatorOutpatientLibrary from './components/dashboard/FacilitatorOutpatientLibrary';
import FacilitatorReviewQueue from './components/dashboard/FacilitatorReviewQueue';
import FacilitatorReviewDetail from './components/dashboard/FacilitatorReviewDetail';
import FacilitatorStudentPortfolios from './components/dashboard/FacilitatorStudentPortfolios';
import FacilitatorOutpatientEditor from './components/dashboard/FacilitatorOutpatientEditor';
import EducatorStatsView from './components/dashboard/EducatorStatsView';
import StudentOutpatientWorkspace from './components/dashboard/StudentOutpatientWorkspace';
import StudentPortfolioView from './components/dashboard/StudentPortfolioView';
import StudentPortfolioPrintView from './components/dashboard/StudentPortfolioPrintView';
import PublicPortfolioShareView from './components/dashboard/PublicPortfolioShareView';
import CaseSessionPlayer from './components/player/CaseSessionPlayer';
import LiveSessionView from './components/player/LiveSessionView';
import AppFooter from './components/AppFooter';
import PrivacyPolicyModal from './components/PrivacyPolicyModal';
import { api, createSessionEventSource } from './lib/api';
import { useCurrentTenant } from './lib/TenantBrandingContext';
import UpgradePage from './components/billing/UpgradePage';
import SubscriptionSettings from './components/billing/SubscriptionSettings';
import { createDraftCaseStudy, getSampleCaseStudy, gradeAnswers, normalizeCaseStudy } from './lib/caseStudy';

const STORAGE_KEY = 'case-study-auth';
const SESSION_KEY = 'case-study-live-session';
const PARTICIPANT_KEY = 'case-study-live-participant';
const REFERRAL_KEY = 'case-study-referral';
const getPendingSharedCaseId = () => new URLSearchParams(window.location.search).get('sharedCase');
const getPendingPresentationCaseId = () => new URLSearchParams(window.location.search).get('presentCase');
const isPortfolioPrintRoute = () => window.location.pathname === '/portfolio/print';
const isPortfolioShareRoute = () => window.location.pathname.startsWith('/portfolio/share/');
const hydrateDraftFromCaseStudy = (caseStudy) => normalizeCaseStudy({
  ...(caseStudy?.draftData || {}),
  id: caseStudy?.id || '',
});

const App = () => {
  const { tenant, applyTenantUpdate } = useCurrentTenant();
  const [currentView, setCurrentView] = useState('public');
  // Set when the app loads from a "?exercise=..." share link. Consumed by
  // StudentDrillCatalog to auto-open the detail modal once drills load.
  const [pendingExerciseShareId, setPendingExerciseShareId] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('exercise') || null;
    } catch { return null; }
  });
  // Same trick for "?mcq=..." share links → StudentMcqCatalog auto-opens.
  const [pendingMcqShareId, setPendingMcqShareId] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('mcq') || null;
    } catch { return null; }
  });
  const [joinCode] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('join') || null; } catch { return null; }
  });
  // Capture ?ref= referral code from URL into localStorage so it survives to registration.
  useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref');
      if (ref) {
        window.localStorage.setItem(REFERRAL_KEY, ref.trim().toUpperCase());
        // Strip from URL without reloading
        params.delete('ref');
        const newSearch = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));
      }
    } catch { /* ignore */ }
  });
  const [inviteContext, setInviteContext] = useState(null);
  const [loginModal, setLoginModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authLoading, setAuthLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [studentError, setStudentError] = useState('');
  const [notice, setNotice] = useState(null);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [subscriptionModal, setSubscriptionModal] = useState(false);
  const [token, setToken] = useState(() => window.localStorage.getItem(STORAGE_KEY) || '');
  const [user, setUser] = useState(null);
  // True while the initial /api/auth/me hydrate is in flight so we don't
  // briefly render the public landing for a user who is actually signed in.
  // Initialise from the same localStorage check as `token` so the very first
  // paint already knows we're hydrating, no flash.
  const [hydrating, setHydrating] = useState(() => Boolean(window.localStorage.getItem(STORAGE_KEY)));
  const [caseStudies, setCaseStudies] = useState([]);
  const [caseStudySets, setCaseStudySets] = useState([]);
  const [userAccounts, setUserAccounts] = useState([]);
  const [library, setLibrary] = useState([]);
  const [studentCaseStudySets, setStudentCaseStudySets] = useState([]);
  const [drugLibrary, setDrugLibrary] = useState({ items: [], metadata: { routes: [], frequencies: [], nonAdmins: [] } });
  const [commonConditions, setCommonConditions] = useState([]);
  const [studentSessions, setStudentSessions] = useState([]);
  const [studentLiveSessions, setStudentLiveSessions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [resultsCaseId, setResultsCaseId] = useState('');
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [savingAttemptReview, setSavingAttemptReview] = useState(false);
  const [draft, setDraft] = useState(() => createDraftCaseStudy());
  const [savedDraft, setSavedDraft] = useState(() => createDraftCaseStudy());
  const [activeCaseSession, setActiveCaseSession] = useState(null);
  const [caseTestReturnView, setCaseTestReturnView] = useState('educator-library');
  const [caseAuthoringReturnView, setCaseAuthoringReturnView] = useState('educator-library');
  const [grading, setGrading] = useState(null);
  const [sessionInput, setSessionInput] = useState('');
  const [connectedSessionCode, setConnectedSessionCode] = useState(() => window.localStorage.getItem(SESSION_KEY) || '');
  const [liveSessionCode, setLiveSessionCode] = useState('');
  const [liveSessionState, setLiveSessionState] = useState(null);
  const [liveResponses, setLiveResponses] = useState({});
  const [studentCaseStudy, setStudentCaseStudy] = useState(getSampleCaseStudy());
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [recentPatients, setRecentPatients] = useState([]);
  const [recentPatientAccesses, setRecentPatientAccesses] = useState([]);
  const [patientSearchState, setPatientSearchState] = useState('idle');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const isEducatorUser = user?.role === 'educator' || user?.role === 'educator_admin';
  const isEducatorAdmin = user?.role === 'educator_admin';
  const isPlatformAdmin = user?.role === 'platform_admin';
  const [participantId] = useState(() => {
    const existing = window.localStorage.getItem(PARTICIPANT_KEY);
    if (existing) {
      return existing;
    }
    const created = window.crypto?.randomUUID?.() || `guest-${Date.now()}`;
    window.localStorage.setItem(PARTICIPANT_KEY, created);
    return created;
  });

  useEffect(() => {
    if (!notice) {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(timeoutId);
  }, [notice]);

  useEffect(() => {
    if (!token) {
      setLoginModal(false);
    }
  }, [token]);

  // Edge / Chrome restore an SPA from the back-forward cache (bfcache) with
  // its prior React state intact. That meant a user could log out as admin,
  // log in as a student, hit Back, and find themselves staring at the admin
  // workspace because React state from the admin session was restored
  // wholesale. Force a fresh load on bfcache restore so hydrate runs against
  // whatever token is actually in localStorage right now.
  useEffect(() => {
    const onPageShow = (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // SPA history management. Without this the browser back button leaves
  // the SPA entirely (all our setCurrentView calls are invisible to the
  // browser, so there is nothing to go 'back' to inside the app, only the
  // URL the user landed on before the SPA loaded — often a different
  // tenant or marketing page). Push a history entry whenever the user
  // navigates to a new in-app view, and respond to popstate by snapping
  // currentView back to whatever the previous entry pointed at.
  const isPopNavigation = useRef(false);
  useEffect(() => {
    if (isPopNavigation.current) {
      isPopNavigation.current = false;
      return;
    }
    if (currentView && currentView !== 'public') {
      try {
        window.history.pushState({ spaView: currentView }, '', window.location.pathname + window.location.search);
      } catch (_error) {
        // Quota exceptions or sandboxed iframes — push best-effort, not load-bearing.
      }
    }
  }, [currentView]);

  useEffect(() => {
    // First load: stamp the existing entry so the very first browser Back
    // navigates within the SPA rather than off-site.
    if (typeof window !== 'undefined' && !window.history.state?.spaView) {
      try {
        window.history.replaceState({ spaView: currentView || 'public' }, '', window.location.pathname + window.location.search);
      } catch (_error) {
        // best-effort
      }
    }
    const onPopState = (event) => {
      const nextView = event.state?.spaView;
      if (nextView) {
        isPopNavigation.current = true;
        setCurrentView(nextView);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []); // intentionally one-shot setup

  const showNotice = (message, variant = 'success') => setNotice({ message, variant });

  const normaliseStudentSessionRecord = useCallback((sessionRecord, fallback = {}) => ({
    id: sessionRecord?.id || fallback.id,
    caseStudyId: sessionRecord?.caseStudyId || sessionRecord?.case_study_id || fallback.caseStudyId,
    title: sessionRecord?.title || fallback.title || 'Case study',
    summary: sessionRecord?.summary || fallback.summary || '',
    status: sessionRecord?.status || fallback.status || 'in_progress',
    answers: sessionRecord?.answers || fallback.answers || {},
    progress: sessionRecord?.progress || fallback.progress || {},
    score: sessionRecord?.score ?? fallback.score ?? null,
    facilitatorMark: sessionRecord?.facilitatorMark ?? sessionRecord?.facilitator_mark ?? fallback.facilitatorMark ?? null,
    facilitatorFeedback: sessionRecord?.facilitatorFeedback || sessionRecord?.facilitator_feedback || fallback.facilitatorFeedback || '',
    facilitatorMarkedAt: sessionRecord?.facilitatorMarkedAt || sessionRecord?.facilitator_marked_at || fallback.facilitatorMarkedAt || null,
    facilitatorMarkedByName: sessionRecord?.facilitatorMarkedByName || fallback.facilitatorMarkedByName || '',
    attemptNumber: sessionRecord?.attemptNumber || sessionRecord?.attempt_number || fallback.attemptNumber || null,
    startedAt: sessionRecord?.startedAt || sessionRecord?.started_at || fallback.startedAt || null,
    completedAt: sessionRecord?.completedAt || sessionRecord?.completed_at || fallback.completedAt || null,
    updatedAt: sessionRecord?.updatedAt || sessionRecord?.updated_at || fallback.updatedAt || null,
  }), []);

  const upsertStudentSession = useCallback((sessionRecord, fallback = {}) => {
    const normalised = normaliseStudentSessionRecord(sessionRecord, fallback);
    if (!normalised.id) {
      return;
    }

    setStudentSessions((current) => {
      const remaining = current.filter((item) => item.id !== normalised.id);
      return [normalised, ...remaining].sort((left, right) => {
        const leftTime = new Date(left.updatedAt || left.startedAt || 0).getTime();
        const rightTime = new Date(right.updatedAt || right.startedAt || 0).getTime();
        return rightTime - leftTime;
      });
    });
  }, [normaliseStudentSessionRecord]);

  const refreshDrugLibrary = async () => {
    const response = await api.getDrugLibrary();
    setDrugLibrary(response);
  };

  const refreshCommonConditions = useCallback(async (authToken = token) => {
    if (!authToken) {
      setCommonConditions([]);
      return;
    }

    const response = await api.getCommonConditions(authToken);
    setCommonConditions(response.conditions || []);
  }, [token]);

  const refreshUserAccounts = useCallback(async (authToken = token) => {
    if (!authToken) {
      setUserAccounts([]);
      return;
    }

    const response = await api.listUserAccounts(authToken);
    setUserAccounts(response.users || []);
  }, [token]);

  const refreshEducatorData = async (authToken, preferredCaseId = null) => {
    const [response, setsResponse] = await Promise.all([
      api.listCaseStudyWorkspace(authToken),
      api.listCaseStudySetsWorkspace(authToken),
    ]);
    setCaseStudies(response.ownedCaseStudies || []);
    setCaseStudySets(setsResponse.caseStudySets || []);
    const selectedCase = (response.ownedCaseStudies || []).find((item) => item.id === preferredCaseId)
      || (response.ownedCaseStudies || [])[0]
      || null;

    if (selectedCase) {
      const nextDraft = hydrateDraftFromCaseStudy(selectedCase);
      setDraft(nextDraft);
      setSavedDraft(nextDraft);
      setLiveSessionCode(selectedCase.activeSessionCode || '');
      const analyticsResponse = await api.getCaseAnalytics(selectedCase.id, authToken);
      setAnalytics(analyticsResponse.analytics);
      if (selectedCase.activeSessionCode) {
        const liveResponse = await api.getSession(selectedCase.activeSessionCode);
        setLiveSessionState(liveResponse.session);
        const responses = await api.getSessionResponses(selectedCase.activeSessionCode);
        setLiveResponses(responses.summary || {});
      } else {
        setLiveSessionState(null);
        setLiveResponses({});
      }
    } else {
      const emptyDraft = createDraftCaseStudy();
      setDraft(emptyDraft);
      setSavedDraft(emptyDraft);
      setLiveSessionCode('');
      setLiveSessionState(null);
      setLiveResponses({});
      setAnalytics(null);
    }
    return response;
  };

  const refreshStudentData = useCallback(async (authToken) => {
    const [libraryResult, sessionsResult] = await Promise.allSettled([
      api.listLibrary(authToken),
      api.listMySessions(authToken),
    ]);

    if (libraryResult.status === 'fulfilled') {
      setLibrary((libraryResult.value.caseStudies || []).slice().sort((left, right) => {
        if (Boolean(left.isFavourite) !== Boolean(right.isFavourite)) {
          return left.isFavourite ? -1 : 1;
        }
        return String(left.title || '').localeCompare(String(right.title || ''));
      }));
      setStudentCaseStudySets(libraryResult.value.caseStudySets || []);
    }

    if (sessionsResult.status === 'fulfilled') {
      setStudentSessions(sessionsResult.value.sessions || []);
      setStudentLiveSessions(sessionsResult.value.liveSessions || []);
    }
  }, []);

  const refreshStudentSessions = useCallback(async (authToken = token) => {
    if (!authToken) {
      setStudentSessions([]);
      setStudentLiveSessions([]);
      return;
    }

    const response = await api.listMySessions(authToken);
    setStudentSessions(response.sessions || []);
    setStudentLiveSessions(response.liveSessions || []);
  }, [token]);

  const refreshRecentPatients = useCallback(async () => {
    const response = await api.listRecentTestPatients(token);
    setRecentPatients(response.patients || []);
  }, [token]);

  const refreshRecentPatientAccesses = useCallback(async () => {
    const response = await api.listRecentPatientAccesses(token);
    setRecentPatientAccesses(response.patients || []);
  }, [token]);

  useEffect(() => {
    if (!joinCode) return;
    api.getJoinLinkInfo(joinCode).then((info) => {
      setInviteContext({ cohortName: info.cohortName, tenantDisplayName: info.tenantDisplayName, inviteCode: joinCode });
      setAuthMode('register');
      setCurrentView('join-register');
    }).catch(() => {
      setCurrentView('public');
    });
  }, [joinCode]);

  useEffect(() => {
    if (!token) {
      setUser(null);
      setHydrating(false);
      setCaseStudies([]);
      setCaseStudySets([]);
      setUserAccounts([]);
      setLibrary([]);
      setStudentCaseStudySets([]);
      setStudentSessions([]);
      setStudentLiveSessions([]);
      setSavedDraft(createDraftCaseStudy());
      setResultsCaseId('');
      setSelectedAttempt(null);
      setRecentPatients([]);
      setRecentPatientAccesses([]);
      setCommonConditions([]);
      return undefined;
    }

    let cancelled = false;
    setHydrating(true);
    const hydrate = async () => {
      try {
        const pendingSharedCaseId = getPendingSharedCaseId();
        const pendingPresentationCaseId = getPendingPresentationCaseId();
        const me = await api.me(token);
        if (cancelled) return;
        setUser(me.user);
        await Promise.allSettled([refreshDrugLibrary(), refreshCommonConditions(token)]);

        if (me.user.role === 'platform_admin') {
          if (!cancelled) setCurrentView('platform-admin');
        } else if (me.user.role === 'educator' || me.user.role === 'educator_admin') {
          // Pre-load module workspaces so the dashboard tiles can show
          // accurate counts in their eyebrow badges. Calling the API
          // directly with the fresh token rather than the wrapped
          // refresh* callbacks: those callbacks gate on `isEducatorUser`
          // which is derived from React state and hasn't propagated yet
          // (we just called setUser above but it's not flushed). The
          // wrapped callbacks would silently bail with stale state.
          await Promise.allSettled([
            refreshEducatorData(token, pendingPresentationCaseId || null),
            me.user.role === 'educator_admin' ? refreshUserAccounts(token) : Promise.resolve(),
            api.listDrillsWorkspace(token)
              .then((r) => { if (!cancelled) setDrillsWorkspace(r.drills || []); })
              .catch(() => { if (!cancelled) setDrillsWorkspace([]); }),
            api.listMcqExercisesWorkspace(token)
              .then((r) => { if (!cancelled) setMcqWorkspace(r.exercises || []); })
              .catch(() => { if (!cancelled) setMcqWorkspace([]); }),
            api.listSpotErrorDrillsAll(token)
              .then((r) => { if (!cancelled) setSpotErrorWorkspace(r.drills || []); })
              .catch(() => { if (!cancelled) setSpotErrorWorkspace([]); }),
            api.listOutpatientDrillsAll(token)
              .then((r) => { if (!cancelled) setOutpatientWorkspace(r.drills || []); })
              .catch(() => { if (!cancelled) setOutpatientWorkspace([]); }),
          ]);
          if (!cancelled) setCurrentView(pendingPresentationCaseId ? 'educator-present' : 'educator-home');
        } else {
          await Promise.allSettled([
            refreshStudentData(token),
            refreshRecentPatients(),
            refreshRecentPatientAccesses(),
          ]);
          if (!cancelled && !pendingSharedCaseId) {
            setCurrentView(isPortfolioPrintRoute() ? 'student-portfolio-print' : 'student-home');
          }
        }
      } catch (_error) {
        if (!cancelled) {
          window.localStorage.removeItem(STORAGE_KEY);
          setToken('');
          setUser(null);
        }
      } finally {
        if (!cancelled) setHydrating(false);
      }
    };

    hydrate();
    return () => {
      cancelled = true;
    };
  }, [refreshCommonConditions, refreshRecentPatientAccesses, refreshRecentPatients, refreshStudentData, refreshUserAccounts, token]);

  useEffect(() => {
    if (token) {
      return undefined;
    }

    let cancelled = false;
    const hydrateLibrary = async () => {
      try {
        const response = await api.getDrugLibrary();
        if (!cancelled) {
          setDrugLibrary(response);
        }
      } catch (_error) {
        if (!cancelled) {
          setDrugLibrary({ items: [], metadata: { routes: [], frequencies: [], nonAdmins: [] } });
        }
      }
    };

    hydrateLibrary();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!connectedSessionCode) {
      return undefined;
    }

    window.localStorage.setItem(SESSION_KEY, connectedSessionCode);
    let eventSource;
    let pollTimer;
    let cancelled = false;

    const applyLiveSession = (session) => {
      setLiveSessionState(session);
      setStudentCaseStudy(normalizeCaseStudy(session.payload));
      setStudentError('');
    };

    const pollLiveSession = async () => {
      try {
        const response = await api.getSession(connectedSessionCode);
        if (cancelled) return;
        applyLiveSession(response.session);
      } catch (_error) {
        // EventSource error messaging is enough here; the poller quietly retries.
      }
    };

    const connectToSession = async () => {
      try {
        const response = await api.getSession(connectedSessionCode);
        if (cancelled) return;
        applyLiveSession(response.session);
        if (token && user?.role === 'student') {
          await api.joinSession(connectedSessionCode, participantId, user?.displayName || 'Guest learner', token);
          if (!cancelled) {
            await refreshStudentData(token);
          }
        }
        const responses = await api.getSessionResponses(connectedSessionCode);
        if (cancelled) return;
        setLiveResponses(responses.summary || {});
        setStudentError('');
        eventSource = createSessionEventSource(connectedSessionCode);
        eventSource.addEventListener('case-update', (event) => {
          const session = JSON.parse(event.data);
          applyLiveSession(session);
        });
        eventSource.addEventListener('response-update', (event) => {
          setLiveResponses(JSON.parse(event.data));
        });
        eventSource.onopen = () => {
          setStudentError('');
        };
        eventSource.onerror = () => {
          setStudentError('Live updates paused. Try reconnecting to the session.');
        };
        pollTimer = window.setInterval(pollLiveSession, 5000);
      } catch (error) {
        if (!cancelled) {
          setStudentError(error.message);
        }
      }
    };

    connectToSession();
    return () => {
      cancelled = true;
      if (pollTimer) window.clearInterval(pollTimer);
      if (eventSource) eventSource.close();
    };
  }, [connectedSessionCode, participantId, refreshStudentData, token, user]);

  const handleAuthSubmit = async ({ email, password, displayName, mode, role, discipline }) => {
    setAuthLoading(true);
    setAuthError('');
    try {
      const pendingReferralCode = window.localStorage.getItem(REFERRAL_KEY) || null;
      const response = mode === 'login'
        ? await api.login(email, password)
        : await api.register(email, password, displayName, role, discipline, inviteContext?.inviteCode || null, pendingReferralCode);
      if (!response.token || !response.user) {
        setLoginModal(false);
        showNotice(
          response.message || 'Registration request received.',
          response.user?.accountStatus === 'pending_approval' ? 'info' : 'success'
        );
        return;
      }
      window.localStorage.setItem(STORAGE_KEY, response.token);
      window.localStorage.removeItem(REFERRAL_KEY);
      if (response.user.role === 'student') {
        await Promise.allSettled([
          refreshStudentData(response.token),
          refreshStudentSessions(response.token),
        ]);
      }
      setToken(response.token);
      setUser(response.user);
      setLoginModal(false);
      const nextView = response.user.role === 'platform_admin'
        ? 'platform-admin'
        : (response.user.role === 'educator' || response.user.role === 'educator_admin')
          ? 'educator-home'
          : 'student-home';
      setCurrentView(nextView);
      showNotice(mode === 'login' ? 'Signed in successfully.' : 'Account created successfully.');
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    // Server-side revocation first. JWTs are stateless so without this the
    // same token still works in any other tab / subdomain that has it cached.
    // Fire-and-forget; localStorage is cleared whether the call succeeds or
    // fails (network error shouldn't trap the user in a signed-in UI).
    if (token) {
      api.logout(token).catch(() => {});
    }
    window.localStorage.removeItem(STORAGE_KEY);
    setToken('');
    setUser(null);
    setLiveSessionCode('');
    setLiveSessionState(null);
    setLiveResponses({});
    setCaseStudies([]);
    setCaseStudySets([]);
    setLibrary([]);
    setStudentCaseStudySets([]);
    setSavedDraft(createDraftCaseStudy());
    setResultsCaseId('');
    setSelectedAttempt(null);
    setActiveCaseSession(null);
    setGrading(null);
    setCurrentView('public');
    // Replace the current history entry with the root URL so the browser
    // Back button can't bring the user back to a deep authed page (where
    // the bfcache restore would just re-show the cached UI). location.replace
    // also forces a fresh document load, so no stale React state survives.
    if (typeof window !== 'undefined' && window.location) {
      window.location.replace(`${window.location.origin}/`);
    }
  };

  const handleForgotPassword = () => {
    showNotice('Password reset is not configured in this demo yet. Use the demo credentials or create a new account.', 'info');
  };

  const handleUpdateDiscipline = async (nextDiscipline) => {
    try {
      const result = await api.updateMyDiscipline(nextDiscipline, token);
      setUser((current) => (current ? { ...current, discipline: result?.discipline ?? null } : current));
      showNotice('Study area updated.', 'info');
    } catch (err) {
      showNotice(err?.message || 'Could not update study area.', 'danger');
    }
  };

  const handleOpenStudentCases = useCallback(async () => {
    if (token && user?.role === 'student') {
      await Promise.allSettled([
        refreshStudentData(token),
        refreshStudentSessions(token),
      ]);
    }
    setCurrentView('student-cases');
  }, [refreshStudentData, refreshStudentSessions, token, user]);

  useEffect(() => {
    if (currentView !== 'student-cases' || !token || user?.role !== 'student') {
      return;
    }

    refreshStudentSessions(token).catch(() => {
      showNotice('Unable to refresh saved case sessions right now.', 'warning');
    });
  }, [currentView, refreshStudentSessions, token, user]);

  const handleSave = async (nextDraft) => {
    setSaveLoading(true);
    try {
      const response = nextDraft.id ? await api.updateCaseStudy(nextDraft.id, nextDraft, token) : await api.createCaseStudy(nextDraft, token);
      const nextSavedDraft = hydrateDraftFromCaseStudy(response.caseStudy);
      setDraft(nextSavedDraft);
      setSavedDraft(nextSavedDraft);
      await refreshEducatorData(token, nextSavedDraft.id);
      if (liveSessionCode) {
        const liveResponse = await api.pushSession(liveSessionCode, nextSavedDraft, token);
        setLiveSessionState(liveResponse.session);
      }
      const savedCaseMeta = response.caseStudy || {};
      if (savedCaseMeta.status === 'self_paced' && savedCaseMeta.studentAccessEnabled) {
        showNotice('Changes saved. Update self-paced publishing from View and share case studies when ready.', 'warning');
      } else if (savedCaseMeta.status === 'live_classroom') {
        showNotice('Changes saved. Update the live classroom from View and share case studies when ready.', 'warning');
      } else {
        showNotice(nextDraft.id ? 'Case study updated.' : 'Case study created.');
      }
      return nextSavedDraft;
    } catch (error) {
      setAuthError(error.message);
      setLoginModal(true);
      return null;
    } finally {
      setSaveLoading(false);
    }
  };

  const handleLoadCase = async (caseStudyId) => {
    const response = await api.getCaseStudy(caseStudyId, token);
    const loadedDraft = hydrateDraftFromCaseStudy(response.caseStudy);
    setDraft(loadedDraft);
    setSavedDraft(loadedDraft);
    const selected = caseStudies.find((item) => item.id === caseStudyId);
    setLiveSessionCode(selected?.activeSessionCode || '');
    if (selected?.activeSessionCode) {
      const liveResponse = await api.getSession(selected.activeSessionCode);
      setLiveSessionState(liveResponse.session);
      const responses = await api.getSessionResponses(selected.activeSessionCode);
      setLiveResponses(responses.summary || {});
    } else {
      setLiveSessionState(null);
      setLiveResponses({});
    }
    const analyticsResponse = await api.getCaseAnalytics(caseStudyId, token);
    setAnalytics(analyticsResponse.analytics);
    showNotice('Case study loaded.', 'info');
  };

  const handlePublish = async (nextDraft, mode = 'self_paced') => {
    let publishDraft = nextDraft;
    if (!publishDraft.id) {
      publishDraft = await handleSave(nextDraft);
      if (!publishDraft?.id) return;
    }

    setSaveLoading(true);
    try {
      const response = await api.publishCaseStudy(publishDraft.id, publishDraft, mode, token);
      if (response.session) {
        setLiveSessionCode(response.session.sessionCode);
        setLiveSessionState(response.session);
        setLiveResponses({});
        setStudentCaseStudy(normalizeCaseStudy(response.session.payload));
      } else {
        setLiveSessionCode('');
        setLiveSessionState(null);
        setLiveResponses({});
      }
      await refreshEducatorData(token, publishDraft.id);
      if (response.pendingApproval) {
        showNotice('Sent to facilitator-admin for approval before students can access.', 'info');
      } else {
        showNotice(
          mode === 'live_classroom'
            ? `Live classroom started. Code: ${response.session?.sessionCode || ''}`
            : 'Case published for self-paced student access.'
        );
      }
    } catch (err) {
      showNotice(err?.message || 'Failed to publish case.', 'danger');
    } finally {
      setSaveLoading(false);
    }
  };

  const handlePublishFacilitatorCase = async (caseStudyId, mode = 'self_paced') => {
    const selected = caseStudies.find((item) => item.id === caseStudyId);
    if (!selected) {
      showNotice('Case study could not be found.', 'danger');
      return;
    }

    await handlePublish(hydrateDraftFromCaseStudy(selected), mode);
  };

  const openFacilitatorCaseTest = (caseStudy, returnView = 'educator-library') => {
    const testDraft = normalizeCaseStudy(hydrateDraftFromCaseStudy(caseStudy));
    setActiveCaseSession({
      id: `test-${caseStudy?.id || Date.now()}`,
      caseStudyId: caseStudy?.id || '',
      title: `Test: ${caseStudy?.title || testDraft.case_study_name || 'Case study'}`,
      summary: caseStudy?.summary || testDraft.short_description || '',
      status: 'in_progress',
      caseSnapshot: testDraft,
      answers: {},
      progress: {},
      score: null,
      attemptNumber: null,
      startedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isFacilitatorPreview: true,
    });
    setCaseTestReturnView(returnView);
    setGrading(null);
    setCurrentView('educator-test-player');
    showNotice('Test mode opened. This will not publish the case or save a student attempt.', 'info');
  };

  const handleTestFacilitatorCase = (caseStudyId) => {
    const selected = caseStudies.find((item) => item.id === caseStudyId);
    if (!selected) {
      showNotice('Case study could not be found.', 'danger');
      return;
    }
    openFacilitatorCaseTest(selected, 'educator-library');
  };

  const handleTestCurrentDraft = (currentDraft) => {
    const testDraft = normalizeCaseStudy(currentDraft || draft);
    setActiveCaseSession({
      id: `test-draft-${Date.now()}`,
      caseStudyId: testDraft.id || '',
      title: `Test: ${testDraft.case_study_name || 'Unsaved case study'}`,
      summary: testDraft.short_description || '',
      status: 'in_progress',
      caseSnapshot: testDraft,
      answers: {},
      progress: {},
      score: null,
      attemptNumber: null,
      startedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isFacilitatorPreview: true,
    });
    setCaseTestReturnView('educator-create');
    setGrading(null);
    setCurrentView('educator-test-player');
    showNotice('Testing the current draft. This will not publish the case or create a student attempt.', 'info');
  };

  const handleArchiveCase = async (caseId) => {
    await api.archiveCaseStudy(caseId, token);
    await refreshEducatorData(token);
    showNotice('Case archived.', 'info');
  };

  const handleUnarchiveCase = async (caseId) => {
    await api.unarchiveCaseStudy(caseId, token);
    await refreshEducatorData(token, caseId);
    showNotice('Case restored to draft.', 'info');
  };

  const handleScheduleCasePublish = async (caseId, payload) => {
    await api.scheduleCaseStudyPublish(caseId, payload, token);
    await refreshEducatorData(token, caseId);
    const hasAny = payload?.scheduledPublishAt || payload?.scheduledUnpublishAt;
    showNotice(hasAny ? 'Schedule updated.' : 'Schedule cleared.', 'info');
  };

  const handleRefreshAnalytics = async (caseId) => {
    const response = await api.getCaseAnalytics(caseId, token);
    setAnalytics(response.analytics);
    showNotice('Analytics refreshed.', 'info');
  };

  const handleOpenCaseResults = async (caseId) => {
    const response = await api.getCaseAnalytics(caseId, token);
    setAnalytics(response.analytics);
    setResultsCaseId(caseId);
    setSelectedAttempt(null);
    setCurrentView('educator-results');
  };

  const handleOpenFacilitatorPresentation = async (caseId) => {
    const selected = caseStudies.find((item) => item.id === caseId);
    if (!selected?.activeSessionCode) {
      showNotice('Start live classroom for this case first, then open it from the library.', 'warning');
      return;
    }

    await handleLoadCase(caseId);
    setCurrentView('educator-present');
  };

  const handleOpenFacilitatorAttempt = async (caseId, sessionId) => {
    try {
      const response = await api.getFacilitatorCaseAttempt(caseId, sessionId, token);
      setResultsCaseId(caseId);
      setSelectedAttempt(response.attempt);
      setCurrentView('educator-attempt');
    } catch (error) {
      showNotice(error.message || 'Unable to open this student attempt right now.', 'danger');
    }
  };

  const handleSaveFacilitatorAttemptReview = async (caseId, sessionId, mark, feedback) => {
    setSavingAttemptReview(true);
    try {
      const response = await api.saveFacilitatorCaseAttemptReview(caseId, sessionId, mark, feedback, token);
      setSelectedAttempt((current) => (
        current
          ? {
              ...current,
              ...response.attempt,
            }
          : response.attempt
      ));
      showNotice('Facilitator review saved.');
      await handleRefreshAnalytics(caseId);
    } catch (error) {
      showNotice(error.message || 'Unable to save facilitator review.', 'danger');
    } finally {
      setSavingAttemptReview(false);
    }
  };

  const handleResetFacilitatorAttempt = async (caseId, sessionId) => {
    setSavingAttemptReview(true);
    try {
      const response = await api.resetFacilitatorCaseAttempt(caseId, sessionId, token);
      setSelectedAttempt((current) => (
        current?.id === sessionId
          ? {
              ...current,
              ...response.attempt,
            }
          : current
      ));
      showNotice('Student attempt reset. The learner can take this case again.', 'success');
      await handleRefreshAnalytics(caseId);
    } catch (error) {
      showNotice(error.message || 'Unable to reset this student attempt.', 'danger');
    } finally {
      setSavingAttemptReview(false);
    }
  };

  const handlePreviewDrugLibraryImport = async (csvContent, replaceExisting = true) => {
    return api.previewDrugLibraryImport(csvContent, token, replaceExisting);
  };

  const handleImportDrugLibrary = async (csvContent, replaceExisting, confirmedMissing = null) => {
    await api.importDrugLibrary(csvContent, token, replaceExisting, confirmedMissing);
    await refreshDrugLibrary();
    showNotice('Drug library imported.', 'success');
  };

  const handleAddRouteOption = async (label) => {
    await api.createRouteOption(label, token);
    await refreshDrugLibrary();
    showNotice('Route added.', 'success');
  };

  const handleDeleteRouteOption = async (routeId) => {
    await api.deleteRouteOption(routeId, token);
    await refreshDrugLibrary();
    showNotice('Route removed.', 'info');
  };

  const handleUpdateRouteOption = async (routeId, label, sortOrder) => {
    await api.updateRouteOption(routeId, label, sortOrder, token);
    await refreshDrugLibrary();
    showNotice('Route updated.', 'success');
  };

  const handleMoveRouteOption = async (routeId, direction) => {
    const routeOptions = [...(drugLibrary?.metadata?.routeOptions || [])].sort((left, right) => left.sortOrder - right.sortOrder);
    const index = routeOptions.findIndex((item) => item.id === routeId);
    const swapIndex = direction === 'up' ? index - 1 : index + 1;

    if (index < 0 || swapIndex < 0 || swapIndex >= routeOptions.length) {
      return;
    }

    const current = routeOptions[index];
    const swap = routeOptions[swapIndex];

    await api.updateRouteOption(current.id, current.label, swap.sortOrder, token);
    await api.updateRouteOption(swap.id, swap.label, current.sortOrder, token);
    await refreshDrugLibrary();
    showNotice('Route order updated.', 'success');
  };

  const handleImportRouteOptions = async (csvContent) => {
    const response = await api.importRouteOptions(csvContent, token);
    await refreshDrugLibrary();
    showNotice(`${response.importedCount || 0} routes imported.`, 'success');
  };

  const handleAddFrequencyOption = async (label, defaultAdminTimes) => {
    await api.createFrequencyOption(label, defaultAdminTimes, token);
    await refreshDrugLibrary();
    showNotice('Frequency added.', 'success');
  };

  const handleDeleteFrequencyOption = async (frequencyId) => {
    await api.deleteFrequencyOption(frequencyId, token);
    await refreshDrugLibrary();
    showNotice('Frequency removed.', 'info');
  };

  const handleUpdateFrequencyOption = async (frequencyId, label, defaultAdminTimes, sortOrder) => {
    await api.updateFrequencyOption(frequencyId, label, defaultAdminTimes, sortOrder, token);
    await refreshDrugLibrary();
    showNotice('Frequency updated.', 'success');
  };

  const handleMoveFrequencyOption = async (frequencyId, direction) => {
    const frequencyOptions = [...(drugLibrary?.metadata?.frequencyOptions || [])].sort((left, right) => left.sortOrder - right.sortOrder);
    const index = frequencyOptions.findIndex((item) => item.id === frequencyId);
    const swapIndex = direction === 'up' ? index - 1 : index + 1;

    if (index < 0 || swapIndex < 0 || swapIndex >= frequencyOptions.length) {
      return;
    }

    const current = frequencyOptions[index];
    const swap = frequencyOptions[swapIndex];

    await api.updateFrequencyOption(current.id, current.label, (current.defaultAdminTimes || []).join(', '), swap.sortOrder, token);
    await api.updateFrequencyOption(swap.id, swap.label, (swap.defaultAdminTimes || []).join(', '), current.sortOrder, token);
    await refreshDrugLibrary();
    showNotice('Frequency order updated.', 'success');
  };

  const handleAddIndicationOption = async (label) => {
    await api.createIndicationOption(label, token);
    await refreshDrugLibrary();
    showNotice('Indication added.', 'success');
  };

  const handleDeleteIndicationOption = async (indicationId) => {
    await api.deleteIndicationOption(indicationId, token);
    await refreshDrugLibrary();
    showNotice('Indication removed.', 'info');
  };

  const handleUpdateIndicationOption = async (indicationId, label) => {
    await api.updateIndicationOption(indicationId, label, token);
    await refreshDrugLibrary();
    showNotice('Indication updated.', 'success');
  };

  const handleImportIndicationOptions = async (csvContent) => {
    const response = await api.importIndicationOptions(csvContent, token);
    await refreshDrugLibrary();
    showNotice(`${response.importedCount || 0} indications imported.`, 'success');
  };

  const handleAddUnitOption = async (label) => {
    await api.createUnitOption(label, token);
    await refreshDrugLibrary();
    showNotice('Unit added.', 'success');
  };

  const handleDeleteUnitOption = async (unitId) => {
    await api.deleteUnitOption(unitId, token);
    await refreshDrugLibrary();
    showNotice('Unit removed.', 'info');
  };

  const handleUpdateUnitOption = async (unitId, label) => {
    await api.updateUnitOption(unitId, label, token);
    await refreshDrugLibrary();
    showNotice('Unit updated.', 'success');
  };

  const handleImportUnitOptions = async (csvContent) => {
    const response = await api.importUnitOptions(csvContent, token);
    await refreshDrugLibrary();
    showNotice(`${response.importedCount || 0} units imported.`, 'success');
  };

  const handleAddFormOption = async (label) => {
    await api.createFormOption(label, token);
    await refreshDrugLibrary();
    showNotice('Form added.', 'success');
  };

  const handleDeleteFormOption = async (formId) => {
    await api.deleteFormOption(formId, token);
    await refreshDrugLibrary();
    showNotice('Form removed.', 'info');
  };

  const handleUpdateFormOption = async (formId, label) => {
    await api.updateFormOption(formId, label, token);
    await refreshDrugLibrary();
    showNotice('Form updated.', 'success');
  };

  const handleImportFormOptions = async (csvContent) => {
    const response = await api.importFormOptions(csvContent, token);
    await refreshDrugLibrary();
    showNotice(`${response.importedCount || 0} forms imported.`, 'success');
  };

  const handleImportFrequencyOptions = async (csvContent) => {
    const response = await api.importFrequencyOptions(csvContent, token);
    await refreshDrugLibrary();
    showNotice(`${response.importedCount || 0} frequencies imported.`, 'success');
  };

  const handleAddCriticalMedicine = async (drugId) => {
    await api.createCriticalMedicine(drugId, token);
    await refreshDrugLibrary();
    showNotice('Critical medicine added.', 'success');
  };

  const handleDeleteCriticalMedicine = async (criticalMedicineId) => {
    await api.deleteCriticalMedicine(criticalMedicineId, token);
    await refreshDrugLibrary();
    showNotice('Critical medicine removed.', 'info');
  };

  const handleAddControlledDrug = async (drugId) => {
    await api.createControlledDrug(drugId, token);
    await refreshDrugLibrary();
    showNotice('Controlled drug added.', 'success');
  };

  const handleDeleteControlledDrug = async (controlledDrugId) => {
    await api.deleteControlledDrug(controlledDrugId, token);
    await refreshDrugLibrary();
    showNotice('Controlled drug removed.', 'info');
  };

  const handleAddOrderSet = async (payload) => {
    await api.createOrderSet(payload, token);
    await refreshDrugLibrary();
    showNotice('Order set added.', 'success');
  };

  const handleUpdateOrderSet = async (orderSetId, payload) => {
    await api.updateOrderSet(orderSetId, payload, token);
    await refreshDrugLibrary();
    showNotice('Order set updated.', 'success');
  };

  const handleDeleteOrderSet = async (orderSetId) => {
    await api.deleteOrderSet(orderSetId, token);
    await refreshDrugLibrary();
    showNotice('Order set removed.', 'info');
  };

  const handleUpdateDrugLibraryItem = async (drugId, payload) => {
    await api.updateDrugLibraryItem(drugId, payload, token);
    await refreshDrugLibrary();
    showNotice('Drug updated.', 'success');
  };

  const handleCreateDrugLibraryItem = async (payload) => {
    await api.createDrugLibraryItem(payload, token);
    await refreshDrugLibrary();
    showNotice('Drug added.', 'success');
  };

  const handleDeleteDrugLibraryItem = async (drugId) => {
    await api.deleteDrugLibraryItem(drugId, token);
    await refreshDrugLibrary();
    showNotice('Drug removed.', 'info');
  };

  const handleSuspendUserAccount = async (userId, reason) => {
    await api.suspendUserAccount(userId, reason, token);
    await refreshUserAccounts(token);
    showNotice('User account suspended.', 'warning');
  };

  const handleRestoreUserAccount = async (userId) => {
    await api.restoreUserAccount(userId, token);
    await refreshUserAccounts(token);
    showNotice('User account restored.', 'success');
  };

  const handleRemoveUserAccountAccess = async (userId, reason) => {
    await api.removeUserAccountAccess(userId, reason, token);
    await refreshUserAccounts(token);
    showNotice('User access removed and retention review scheduled.', 'info');
  };

  const handleSetLiveStep = async (stepIndex) => {
    if (!liveSessionCode || !liveSessionState) return;
    const response = await api.controlSession(
      liveSessionCode,
      stepIndex,
      liveSessionState.revealAnswers,
      token,
      liveSessionState.presentationStage,
      liveSessionState.payload?.currentStageIndex,
      liveSessionState.payload?.revealedQuestionNumbers
    );
    setLiveSessionState(response.session);
    showNotice(`Moved live session to question ${response.session.stepIndex + 1}.`, 'info');
  };

  const handleSetLiveReveal = async (revealAnswers) => {
    if (!liveSessionCode || !liveSessionState) return;
    const response = await api.controlSession(
      liveSessionCode,
      liveSessionState.stepIndex,
      revealAnswers,
      token,
      liveSessionState.presentationStage,
      liveSessionState.payload?.currentStageIndex,
      liveSessionState.payload?.revealedQuestionNumbers
    );
    setLiveSessionState(response.session);
    showNotice(revealAnswers ? 'Answer revealed to students.' : 'Answer hidden from students.', 'info');
  };

  const handleRevealLiveQuestion = async (questionNumber) => {
    if (!liveSessionCode || !liveSessionState || !questionNumber) return;
    const currentRevealed = Array.isArray(liveSessionState.payload?.revealedQuestionNumbers)
      ? liveSessionState.payload.revealedQuestionNumbers.map(String)
      : [];
    const nextRevealed = currentRevealed.includes(String(questionNumber))
      ? currentRevealed.filter((item) => item !== String(questionNumber))
      : [...currentRevealed, String(questionNumber)];
    const response = await api.controlSession(
      liveSessionCode,
      liveSessionState.stepIndex,
      liveSessionState.revealAnswers,
      token,
      liveSessionState.presentationStage,
      liveSessionState.payload?.currentStageIndex,
      nextRevealed
    );
    setLiveSessionState(response.session);
    showNotice(nextRevealed.includes(String(questionNumber)) ? 'Answer revealed for this question.' : 'Answer hidden for this question.', 'info');
  };

  const handleSetLivePresentationStage = async (presentationStage) => {
    if (!liveSessionCode || !liveSessionState) return;
    const response = await api.controlSession(
      liveSessionCode,
      liveSessionState.stepIndex,
      liveSessionState.revealAnswers,
      token,
      presentationStage,
      liveSessionState.payload?.currentStageIndex,
      liveSessionState.payload?.revealedQuestionNumbers
    );
    setLiveSessionState(response.session);
    showNotice(
      presentationStage === 'initial' ? 'Presentation returned to the initial scenario.' : 'More case information has been revealed.',
      'info'
    );
  };

  const handleSetLiveStageIndex = async (stageIndex) => {
    if (!liveSessionCode || !liveSessionState) return;
    const response = await api.controlSession(
      liveSessionCode,
      liveSessionState.stepIndex,
      liveSessionState.revealAnswers,
      token,
      liveSessionState.presentationStage,
      stageIndex,
      liveSessionState.payload?.revealedQuestionNumbers
    );
    setLiveSessionState(response.session);
    showNotice(`Moved live case study to stage ${response.session.payload?.currentStageIndex + 1}.`, 'info');
  };

  const handleStartCase = async (caseId) => {
    try {
      const started = await api.startCaseSession(caseId, token);
      const loaded = await api.getCaseSession(started.session.id, token);
      setActiveCaseSession(loaded.session);
      upsertStudentSession(loaded.session);
      setGrading(null);
      setCurrentView('player');
      await refreshStudentData(token);
      showNotice(
        loaded.session?.attemptNumber ? `Attempt ${loaded.session.attemptNumber} opened.` : 'Case session opened.',
        'info'
      );
    } catch (error) {
      // Phase 5: paywall response — route the user to the upgrade page
      if (error?.status === 402) {
        showNotice(error.message || 'This case requires an active subscription.', 'warning');
        setCurrentView('upgrade');
        return;
      }
      await refreshStudentData(token);
      showNotice(error.message || 'Unable to start this case study.', 'warning');
    }
  };

  const handleResumeSession = async (sessionId) => {
    const loaded = await api.getCaseSession(sessionId, token);
    setActiveCaseSession(loaded.session);
    setGrading(null);
    setCurrentView('player');
    showNotice('Session loaded.', 'info');
  };

  const handleSaveCaseSession = async (answers, progress, caseSnapshot) => {
    if (!activeCaseSession) return;
    if (activeCaseSession.isFacilitatorPreview) {
      setActiveCaseSession((current) => ({
        ...current,
        answers,
        progress,
        caseSnapshot: caseSnapshot || current.caseSnapshot,
        updatedAt: new Date().toISOString(),
      }));
      showNotice('Test progress kept in this preview only.', 'info');
      return;
    }
    setSaveLoading(true);
    try {
      const updated = await api.saveCaseSession(activeCaseSession.id, answers, progress, token, caseSnapshot);
      const savedCaseSnapshot = updated.session.case_snapshot || updated.session.caseSnapshot || caseSnapshot || activeCaseSession.caseSnapshot;
      const nextSession = {
        ...activeCaseSession,
        answers,
        progress,
        caseSnapshot: savedCaseSnapshot,
        status: 'in_progress',
        updatedAt: updated.session.updated_at || activeCaseSession.updatedAt,
      };
      setActiveCaseSession(nextSession);
      upsertStudentSession(updated.session, nextSession);
      await refreshStudentData(token);
      showNotice('Progress saved.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSubmitCaseSession = async (answers, progress, caseSnapshot) => {
    if (!activeCaseSession) return;
    if (activeCaseSession.isFacilitatorPreview) {
      const grade = gradeAnswers(caseSnapshot || activeCaseSession.caseSnapshot, answers);
      setActiveCaseSession((current) => ({
        ...current,
        answers,
        caseSnapshot: caseSnapshot || current.caseSnapshot,
        progress: { ...progress, breakdown: grade.breakdown },
        status: 'completed',
        score: grade.score,
        completedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }));
      setGrading(grade);
      showNotice(`Test submitted. Score: ${grade.score}%`, 'info');
      return;
    }
    setSaveLoading(true);
    try {
      const completed = await api.completeCaseSession(activeCaseSession.id, answers, progress, token, caseSnapshot);
      const savedCaseSnapshot = completed.session.case_snapshot || completed.session.caseSnapshot || caseSnapshot || activeCaseSession.caseSnapshot;
      const nextSession = {
        ...activeCaseSession,
        answers,
        caseSnapshot: savedCaseSnapshot,
        progress: { ...progress, breakdown: completed.grading.breakdown },
        status: 'completed',
        score: completed.grading.score,
        completedAt: completed.session.completed_at || activeCaseSession.completedAt,
        updatedAt: completed.session.updated_at || activeCaseSession.updatedAt,
      };
      setActiveCaseSession(nextSession);
      upsertStudentSession(completed.session, nextSession);
      setGrading(completed.grading);
      await refreshStudentData(token);
      showNotice(`Case submitted. Score: ${completed.grading.score}%`);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleJoinLive = async (event, sessionCodeOverride = '') => {
    if (event?.preventDefault) {
      event.preventDefault();
    }
    const rawSessionCode = String(sessionCodeOverride || sessionInput || '').trim();
    if (!rawSessionCode) {
      setStudentError('Enter a live session code to join the class case.');
      return false;
    }
    const nextSessionCode = rawSessionCode.toUpperCase();
    if (token && user?.role === 'student') {
      try {
        await api.joinSession(nextSessionCode, participantId, user?.displayName || 'Guest learner', token);
        await refreshStudentData(token);
      } catch (error) {
        setStudentError(error.message || 'Unable to join this live session right now.');
        showNotice(error.message || 'Unable to join this live session right now.', 'danger');
        return false;
      }
    }
    setConnectedSessionCode(nextSessionCode);
    setCurrentView('live');
    showNotice(`Joined live session ${nextSessionCode}.`, 'info');
    return true;
  };

  const handleLeaveLive = async () => {
    window.localStorage.removeItem(SESSION_KEY);
    setConnectedSessionCode('');
    setSessionInput('');
    setLiveSessionState(null);
    setLiveResponses({});
    setStudentError('');
    if (token && user?.role === 'student') {
      await refreshStudentData(token);
    }
    setCurrentView(user?.role === 'student' ? 'student-home' : 'public');
    showNotice('Left the live session.', 'info');
  };

  const handleSubmitLiveResponse = async (questionNumber, answer) => {
    if (!connectedSessionCode) return;
    const participantName = user?.displayName || 'Guest learner';
    const response = await api.submitSessionResponse(
      connectedSessionCode,
      questionNumber,
      answer,
      participantId,
      participantName,
      token || undefined
    );
    setLiveResponses(response.summary || {});
    if (response.session) {
      setLiveSessionState(response.session);
      setStudentCaseStudy(normalizeCaseStudy(response.session.payload));
    }
    if (token && user?.role === 'student') {
      await refreshStudentData(token);
    }
    showNotice('Live answer submitted.', 'success');
  };

  const handleToggleFavouriteCaseStudy = async (caseStudyId, isFavourite) => {
    if (isFavourite) {
      await api.unfavouriteCaseStudy(caseStudyId, token);
      showNotice('Removed from favourites.', 'info');
    } else {
      await api.favouriteCaseStudy(caseStudyId, token);
      showNotice('Added to favourites.', 'success');
    }
    await refreshStudentData(token);
  };

  const handleRejoinLiveSession = async (sessionCode) => {
    setSessionInput(sessionCode);
    await handleJoinLive({ preventDefault() {} }, sessionCode);
  };

  const handleSearchPatient = async (searchFields) => {
    const hasCriteria = Object.values(searchFields || {}).some((value) => String(value || '').trim());
    if (!hasCriteria) {
      setPatientSearchState('idle');
      setPatientSearchResults([]);
      return;
    }

    const response = await api.searchTestPatients(searchFields, token);
    setPatientSearchResults(response.patients);
    setPatientSearchState(response.patients.length ? 'results' : 'not-found');
  };

  const handleSelectPatient = async (patientId) => {
    const [response] = await Promise.all([
      api.getTestPatient(patientId, token),
      refreshDrugLibrary(),
    ]);
    setSelectedPatient(response.patient);
    await Promise.all([refreshRecentPatients(), refreshRecentPatientAccesses()]);
    setCurrentView('student-epma');
  };

  const handleCreateTestPatient = async (patientData) => {
    const [response] = await Promise.all([
      api.createTestPatient(patientData, token),
      refreshDrugLibrary(),
    ]);
    setSelectedPatient(response.patient);
    setPatientSearchResults([]);
    setPatientSearchState('idle');
    await Promise.all([refreshRecentPatients(), refreshRecentPatientAccesses()]);
    setCurrentView('student-epma');
    showNotice(`Test patient ${response.patient.fullName} admitted.`, 'success');
  };

  const handleOpenEpmaWorkspace = async () => {
    setSelectedPatient(null);
    setPatientSearchResults([]);
    setPatientSearchState('idle');
    await Promise.all([refreshRecentPatients(), refreshRecentPatientAccesses(), refreshDrugLibrary()]);
    setCurrentView('student-epma');
  };

  // ----- Spot-the-Error authoring (educator-side) -----
  const [spotErrorWorkspace, setSpotErrorWorkspace] = useState([]);
  const [editingSpotErrorDrill, setEditingSpotErrorDrill] = useState(null);
  const [spotErrorEditorSaving, setSpotErrorEditorSaving] = useState(false);
  const [spotErrorEditorError, setSpotErrorEditorError] = useState('');

  const refreshSpotErrorWorkspace = useCallback(async () => {
    if (!token || !isEducatorUser) return;
    try {
      const result = await api.listSpotErrorDrillsAll(token);
      setSpotErrorWorkspace(result.drills || []);
    } catch (err) {
      showNotice(err?.message || 'Could not load spot-the-error exercises.', 'danger');
    }
  }, [token, isEducatorUser]);

  const handleOpenEducatorSpotError = async () => {
    await refreshSpotErrorWorkspace();
    setCurrentView('educator-spot-error-library');
  };
  const handleNewSpotErrorDrill = () => {
    setEditingSpotErrorDrill(null);
    setSpotErrorEditorError('');
    setCurrentView('educator-spot-error-edit');
  };
  const handleEditSpotErrorDrill = (drill) => {
    setEditingSpotErrorDrill(drill);
    setSpotErrorEditorError('');
    setCurrentView('educator-spot-error-edit');
  };
  // Memoised so the editor's "fetch full payload" effect doesn't re-fire on
  // every parent render — it was wiping unsaved edits every few seconds when
  // the parent re-rendered.
  const handleLoadFullSpotErrorDrill = useCallback((id) => api.getSpotErrorDrill(id, token), [token]);
  const handleSaveSpotErrorDrill = async (payload) => {
    setSpotErrorEditorSaving(true);
    setSpotErrorEditorError('');
    try {
      const id = editingSpotErrorDrill?.id;
      if (id) {
        await api.updateSpotErrorDrill(id, payload, token);
      } else {
        const created = await api.createSpotErrorDrill(payload, token);
        setEditingSpotErrorDrill({ id: created.id, ...payload });
      }
      await refreshSpotErrorWorkspace();
      showNotice('Spot-the-error exercise saved.', 'success');
      setCurrentView('educator-spot-error-library');
    } catch (err) {
      setSpotErrorEditorError(err?.message || 'Could not save exercise.');
    } finally {
      setSpotErrorEditorSaving(false);
    }
  };
  const handlePublishSpotErrorDrill = async (id) => {
    try {
      await api.publishSpotErrorDrill(id, token);
      await refreshSpotErrorWorkspace();
      showNotice('Drill published — students can now run it.', 'success');
    } catch (err) { showNotice(err?.message || 'Publish failed.', 'danger'); }
  };
  const handleArchiveSpotErrorDrill = async (id) => {
    try {
      await api.archiveSpotErrorDrill(id, token);
      await refreshSpotErrorWorkspace();
      showNotice('Drill archived.', 'info');
    } catch (err) { showNotice(err?.message || 'Archive failed.', 'danger'); }
  };
  const handleDeleteSpotErrorDrill = async (id) => {
    if (!window.confirm('Delete this spot-the-error exercise? Cannot be undone.')) return;
    try {
      await api.deleteSpotErrorDrill(id, token);
      await refreshSpotErrorWorkspace();
      showNotice('Drill deleted.', 'info');
    } catch (err) { showNotice(err?.message || 'Delete failed.', 'danger'); }
  };

  const handleOpenSpotErrorDrills = () => {
    setCurrentView('student-spot-error');
  };

  const [outpatientWorkspace, setOutpatientWorkspace] = useState([]);
  const [editingOutpatientDrill, setEditingOutpatientDrill] = useState(null);
  const [outpatientEditorSaving, setOutpatientEditorSaving] = useState(false);
  const [outpatientEditorError, setOutpatientEditorError] = useState('');
  const [outpatientPreview, setOutpatientPreview] = useState(null);
  // Same preview shape ({ title, payload }) for the spot-the-error editor.
  const [spotErrorPreview, setSpotErrorPreview] = useState(null);
  const [reviewPendingCount, setReviewPendingCount] = useState(0);
  const [reviewSessionId, setReviewSessionId] = useState(null);
  const [reviewModuleType, setReviewModuleType] = useState(null);

  const handleOpenStudentOutpatient = () => {
    setCurrentView('student-outpatient');
  };

  const handleOpenStudentPortfolio = () => {
    setCurrentView('student-portfolio');
  };

  const refreshOutpatientWorkspace = useCallback(async () => {
    if (!token) return;
    try {
      const result = await api.listOutpatientDrillsAll(token);
      setOutpatientWorkspace(result.drills || []);
    } catch (err) {
      showNotice(err?.message || 'Could not load outpatient exercises.', 'danger');
    }
  }, [token, showNotice]);
  const handleOpenEducatorOutpatient = async () => {
    await refreshOutpatientWorkspace();
    setCurrentView('educator-outpatient-library');
  };
  const handleNewOutpatientDrill = () => {
    setEditingOutpatientDrill(null);
    setOutpatientEditorError('');
    setCurrentView('educator-outpatient-edit');
  };
  const handleEditOutpatientDrill = (drill) => {
    setEditingOutpatientDrill(drill);
    setOutpatientEditorError('');
    setCurrentView('educator-outpatient-edit');
  };
  const handleLoadFullOutpatientDrill = useCallback((id) => api.getOutpatientDrill(id, token), [token]);
  const handleSaveOutpatientDrill = async (payload) => {
    setOutpatientEditorSaving(true);
    setOutpatientEditorError('');
    try {
      const id = editingOutpatientDrill?.id;
      if (id) {
        await api.updateOutpatientDrill(id, payload, token);
      } else {
        const created = await api.createOutpatientDrill(payload, token);
        setEditingOutpatientDrill({ id: created.id, ...payload });
      }
      await refreshOutpatientWorkspace();
      showNotice('Outpatient case study saved.', 'success');
      setCurrentView('educator-outpatient-library');
    } catch (err) {
      setOutpatientEditorError(err?.message || 'Could not save exercise.');
    } finally {
      setOutpatientEditorSaving(false);
    }
  };
  const handlePublishOutpatientDrill = async (id) => {
    try {
      await api.publishOutpatientDrill(id, token);
      await refreshOutpatientWorkspace();
      showNotice('Outpatient case study published.', 'success');
    } catch (err) { showNotice(err?.message || 'Publish failed.', 'danger'); }
  };
  const handleArchiveOutpatientDrill = async (id) => {
    try {
      await api.archiveOutpatientDrill(id, token);
      await refreshOutpatientWorkspace();
      showNotice('Outpatient case study archived.', 'info');
    } catch (err) { showNotice(err?.message || 'Archive failed.', 'danger'); }
  };
  const handleDeleteOutpatientDrill = async (id) => {
    if (!window.confirm('Delete this outpatient case study? Cannot be undone.')) return;
    try {
      await api.deleteOutpatientDrill(id, token);
      await refreshOutpatientWorkspace();
      showNotice('Case study deleted.', 'info');
    } catch (err) { showNotice(err?.message || 'Delete failed.', 'danger'); }
  };
  const handlePreviewOutpatientDrill = ({ title, payload }) => {
    setOutpatientPreview({ title, payload });
  };
  const handleExitOutpatientPreview = () => {
    setOutpatientPreview(null);
  };
  const handlePreviewSpotErrorDrill = ({ title, payload }) => {
    setSpotErrorPreview({ title, payload });
  };
  const handleExitSpotErrorPreview = () => {
    setSpotErrorPreview(null);
  };

  const handleUpdatePatientAllergies = async (patientId, payload) => {
    const response = await api.updateTestPatientAllergies(patientId, payload, token);
    setSelectedPatient(response.patient);
    setPatientSearchResults((current) => current.map((item) => (item.id === patientId ? response.patient : item)));
    setRecentPatients((current) => [response.patient, ...current.filter((item) => item.id !== patientId)]);
    showNotice('Allergies updated.', 'success');
  };

  const handleUpdatePatientCaseNotes = async (patientId, payload) => {
    const response = await api.updateTestPatientCaseNotes(patientId, payload, token);
    setSelectedPatient(response.patient);
    setPatientSearchResults((current) => current.map((item) => (item.id === patientId ? response.patient : item)));
    setRecentPatients((current) => [response.patient, ...current.filter((item) => item.id !== patientId)]);
    showNotice(payload?.successMessage || 'Case notes updated.', 'success');
    return response.patient;
  };

  const handleUpdatePatientPrescriptions = async (patientId, prescriptions, options = {}) => {
    const response = await api.updateTestPatientPrescriptions(patientId, prescriptions, token);
    setSelectedPatient(response.patient);
    setPatientSearchResults((current) => current.map((item) => (item.id === patientId ? response.patient : item)));
    setRecentPatients((current) => [response.patient, ...current.filter((item) => item.id !== patientId)]);
    if (!options.suppressToast) {
      showNotice('Prescription chart updated.', 'success');
    }
  };

  const handleUpdateDischargePrescription = async (patientId, dischargePrescription) => {
    const response = await api.updateTestPatientDischargePrescription(patientId, dischargePrescription, token);
    setSelectedPatient(response.patient);
    setPatientSearchResults((current) => current.map((item) => (item.id === patientId ? response.patient : item)));
    setRecentPatients((current) => [response.patient, ...current.filter((item) => item.id !== patientId)]);
  };

  const handleUpdatePatientMeasurements = async (patientId, payload) => {
    const response = await api.updateTestPatientMeasurements(patientId, payload, token);
    setSelectedPatient(response.patient);
    setPatientSearchResults((current) => current.map((item) => (item.id === patientId ? response.patient : item)));
    setRecentPatients((current) => [response.patient, ...current.filter((item) => item.id !== patientId)]);
    showNotice('Measurements updated.', 'success');
    return response.patient;
  };

  const handleDeletePatientMeasurement = async (patientId, measurementId) => {
    const response = await api.deleteTestPatientMeasurement(patientId, measurementId, token);
    setSelectedPatient(response.patient);
    setPatientSearchResults((current) => current.map((item) => (item.id === patientId ? response.patient : item)));
    setRecentPatients((current) => [response.patient, ...current.filter((item) => item.id !== patientId)]);
    showNotice('Measurement deleted.', 'success');
    return response.patient;
  };

  const handleDischargePatient = async (patientId) => {
    try {
      const response = await api.dischargeTestPatient(patientId, token);
      setSelectedPatient(null);
      setPatientSearchResults((current) => current.map((item) => (item.id === patientId ? response.patient : item)));
      await Promise.all([refreshRecentPatients(), refreshRecentPatientAccesses()]);
      setPatientSearchState('idle');
      setCurrentView('student-epma');
      showNotice(`Patient ${response.patient.fullName} discharged.`, 'success');
      return response.patient;
    } catch (error) {
      showNotice(error.message || 'Unable to discharge patient.', 'danger');
      throw error;
    }
  };

  const handleReadmitPatient = async (patientId, payload) => {
    try {
      const response = await api.readmitTestPatient(patientId, payload, token);
      setSelectedPatient(response.patient);
      setPatientSearchResults((current) => current.map((item) => (item.id === patientId ? response.patient : item)));
      await Promise.all([refreshRecentPatients(), refreshRecentPatientAccesses()]);
      setPatientSearchState('idle');
      setCurrentView('student-epma');
      showNotice(`Patient ${response.patient.fullName} re-admitted.`, 'success');
      return response.patient;
    } catch (error) {
      showNotice(error.message || 'Unable to re-admit patient.', 'danger');
      throw error;
    }
  };

  const handleVerifyWitness = async (username, password) => api.verifyWitness(username, password, token);
  const handleBlockedPrescribe = (message) => showNotice(message, 'danger');
  const handleApprovalToast = (message) => showNotice(message, 'success');
  const handleOpenCaseLibrary = () => setCurrentView('educator-library');

  // ----- Calculation drills state -----
  // Legacy: calc-exercises module has been folded into Question library /
  // mcq_exercises. We keep the active-session state because the player
  // surface (`student-drill-player` view) is still used by sessions
  // started via assignments or other side paths that hit /api/drills.
  const [drillsWorkspace, setDrillsWorkspace] = useState([]);
  const [studentDrills, setStudentDrills] = useState([]);
  const [activeDrillSession, setActiveDrillSession] = useState(null);
  // Standalone MCQ module state. Mirrors the drill state above and reuses
  // DrillSessionPlayer for the player surface (route-agnostic).
  const [mcqWorkspace, setMcqWorkspace] = useState([]);
  const [studentRevisionExercises, setStudentRevisionExercises] = useState([]);
  const [studentMcqs, setStudentMcqs] = useState([]);
  const [editingMcq, setEditingMcq] = useState(null);
  const [activeMcqSession, setActiveMcqSession] = useState(null);
  // Per-type Question-bank item library (Phase 1). The workspace list
  // holds every item the educator can see; the library UI filters by
  // active group client-side. activeGroup persists across navigation
  // so the user lands back on the same tab when they return.
  const [qbiWorkspace, setQbiWorkspace] = useState([]);
  const [editingQbi, setEditingQbi] = useState(null);
  const [qbiActiveGroup, setQbiActiveGroup] = useState('mcq');
  const [qbiDefaultType, setQbiDefaultType] = useState('mcq');
  // 'questions' | 'sets' — top-level tab inside FacilitatorQuestionBankItemLibrary.
  const [qbiActiveView, setQbiActiveView] = useState('questions');
  // Currently active assignment when a student starts an exercise from
  // their "Due exercises" surface — passed to the drill start endpoint
  // so the session is attributed back.
  const [activeAssignmentId, setActiveAssignmentId] = useState(null);
  // Educator: assignment selected for the results view.
  const [resultsAssignmentId, setResultsAssignmentId] = useState(null);
  // Student "Due exercises" board on the home page.
  const [myAssignments, setMyAssignments] = useState([]);
  // Mixed-revision (shares the DrillSessionPlayer UI but talks to a
  // different set of endpoints). Active session + cached personal-best map.
  const [activeRevisionSession, setActiveRevisionSession] = useState(null);
  const [revisionBests, setRevisionBests] = useState({});
  const [revisionLengthRange, setRevisionLengthRange] = useState({ min: 5, max: 50, step: 5 });
  const [revisionAvailableCategories, setRevisionAvailableCategories] = useState([]);
  const [revisionAvailableDifficulties, setRevisionAvailableDifficulties] = useState([]);
  const [revisionAvailableQuestionTypes, setRevisionAvailableQuestionTypes] = useState([]);
  const [revisionRecentSessions, setRevisionRecentSessions] = useState([]);
  const refreshDrillsWorkspace = useCallback(async () => {
    if (!token || !isEducatorUser) return;
    try {
      const result = await api.listDrillsWorkspace(token);
      setDrillsWorkspace(result.drills || []);
    } catch (err) {
      showNotice(err?.message || 'Could not load exercises.', 'danger');
    }
  }, [token, isEducatorUser]);

  const refreshStudentDrills = useCallback(async () => {
    if (!token) return;
    try {
      const result = await api.listDrills(token);
      setStudentDrills(result.drills || []);
    } catch (err) {
      // 404 etc — silent on this surface; the catalogue shows an empty state.
      setStudentDrills([]);
    }
  }, [token]);

  // Auto-navigate to the catalog if the URL carries a "?exercise=" share
  // link. Runs once a student user is hydrated; the catalog then opens
  // the detail modal for that exercise (or shows the catalog plainly if
  // the id no longer exists). Strips the param so a refresh doesn't
  // re-trigger.
  useEffect(() => {
    if (!user || user.role !== 'student' || !pendingExerciseShareId) return;
    refreshStudentDrills();
    setCurrentView('student-drill-catalog');
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('exercise');
      window.history.replaceState({}, '', url);
    } catch { /* no-op */ }
  }, [user, pendingExerciseShareId, refreshStudentDrills]);

  // ---------- MCQ exercise handlers ------------------------------------------
  const refreshMcqWorkspace = useCallback(async () => {
    if (!token || !isEducatorUser) return;
    try {
      const result = await api.listMcqExercisesWorkspace(token);
      setMcqWorkspace(result.exercises || []);
    } catch (err) {
      showNotice(err?.message || 'Could not load question sets.', 'danger');
    }
  }, [token, isEducatorUser]);

  const refreshStudentMcqs = useCallback(async () => {
    if (!token) return;
    try {
      const result = await api.listMcqExercises(token);
      setStudentMcqs(result.exercises || []);
    } catch {
      setStudentMcqs([]);
    }
  }, [token]);

  // Auto-navigate to the MCQ catalog if the URL carries "?mcq=" share link.
  useEffect(() => {
    if (!user || user.role !== 'student' || !pendingMcqShareId) return;
    refreshStudentMcqs();
    setCurrentView('student-mcq-catalog');
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('mcq');
      window.history.replaceState({}, '', url);
    } catch { /* no-op */ }
  }, [user, pendingMcqShareId, refreshStudentMcqs]);

  // Sets and items now live under one workspace at `educator-qbi-library`,
  // so the "Open Sets list" handler is a thin alias around the library
  // entry point + tab switch.
  const handleNewMcq = () => {
    setEditingMcq(null);
    setQbiActiveView('sets');
    setCurrentView('educator-mcq-edit');
  };
  const handleEditMcq = (exercise) => {
    setEditingMcq(exercise);
    setQbiActiveView('sets');
    setCurrentView('educator-mcq-edit');
  };
  const handleSaveMcq = async (payload) => {
    const id = editingMcq?.id;
    const result = id ? await api.updateMcqExercise(id, payload, token) : await api.createMcqExercise(payload, token);
    setEditingMcq(result.exercise);
    await refreshMcqWorkspace();
    showNotice('Question set saved.', 'success');
    return result.exercise;
  };
  const handlePublishMcq = async (id) => {
    await api.publishMcqExercise(id, token); await refreshMcqWorkspace();
    showNotice('Question set published — students can now run it.', 'success');
  };
  const handleArchiveMcq = async (id) => {
    await api.archiveMcqExercise(id, token); await refreshMcqWorkspace();
    showNotice('Question set archived.', 'info');
  };
  const handleDeleteMcq = async (id) => {
    if (!window.confirm('Delete this question set? Cannot be undone.')) return;
    await api.deleteMcqExercise(id, token); await refreshMcqWorkspace();
    if (editingMcq?.id === id) {
      setEditingMcq(null);
      setQbiActiveView('sets');
      setCurrentView('educator-qbi-library');
    }
    showNotice('Question set deleted.', 'info');
  };

  // ---------- Question-bank item library (per-type pool) --------------------
  const refreshQbiWorkspace = useCallback(async () => {
    if (!token || !isEducatorUser) return;
    try {
      const result = await api.listQuestionBankItems(token);
      setQbiWorkspace(result.items || []);
    } catch (err) {
      showNotice(err?.message || 'Could not load the question library.', 'danger');
    }
  }, [token, isEducatorUser]);

  const handleOpenQbiLibrary = async () => {
    await Promise.all([refreshQbiWorkspace(), refreshMcqWorkspace()]);
    setCurrentView('educator-qbi-library');
  };
  const handleNewQbi = (type = 'mcq') => {
    setEditingQbi(null);
    setQbiDefaultType(type);
    setCurrentView('educator-qbi-edit');
  };
  const handleEditQbi = (item) => {
    setEditingQbi(item);
    setQbiDefaultType(item?.templateType || 'mcq');
    setCurrentView('educator-qbi-edit');
  };
  const handleSaveQbi = async (payload) => {
    const id = editingQbi?.id;
    const result = id
      ? await api.updateQuestionBankItem(id, payload, token)
      : await api.createQuestionBankItem(payload, token);
    setEditingQbi(result.item);
    await refreshQbiWorkspace();
    showNotice('Question saved.', 'success');
    return result.item;
  };
  const handlePublishQbi = async (id) => {
    await api.publishQuestionBankItem(id, token);
    await refreshQbiWorkspace();
    showNotice('Question published — available to drop into sets.', 'success');
  };
  const handleArchiveQbi = async (id) => {
    await api.archiveQuestionBankItem(id, token);
    await refreshQbiWorkspace();
    showNotice('Question archived.', 'info');
  };
  const handleDeleteQbi = async (id) => {
    if (!window.confirm('Delete this question? Cannot be undone.')) return;
    await api.deleteQuestionBankItem(id, token);
    await refreshQbiWorkspace();
    if (editingQbi?.id === id) {
      setEditingQbi(null);
      setCurrentView('educator-qbi-library');
    }
    showNotice('Question deleted.', 'info');
  };

  // ---------- Revision picker data (Phase 3: folded into Question bank) -----
  // The lecturer no longer authors revision exercises in a dedicated
  // editor — they flag any mcq_exercises set as a curated revision via
  // FacilitatorMcqEditor's "Show in Mixed-revision picker" switch.
  // We still fetch the student-side list to populate the picker modal.
  const refreshStudentRevisionExercises = useCallback(async () => {
    if (!token) return;
    try {
      const result = await api.listRevisionExercises(token);
      setStudentRevisionExercises(result.exercises || []);
    } catch {
      setStudentRevisionExercises([]);
    }
  }, [token]);

  const handleStartCuratedRevision = async (exercise) => {
    try {
      const result = await api.startRevisionExercise(exercise.id, token);
      setActiveRevisionSession(result.session);
      setCurrentView('student-revision-player');
    } catch (err) {
      showNotice(err?.message || 'Could not start this revision exercise.', 'danger');
    }
  };

  const handleOpenStudentMcqs = async () => {
    await refreshStudentMcqs();
    setCurrentView('student-mcq-catalog');
  };
  const handleStartMcq = async (exercise) => {
    try {
      const result = await api.startMcqExercise(exercise.id, token);
      setActiveMcqSession(result.session);
      setCurrentView('student-mcq-player');
    } catch (err) {
      if (err?.status === 402) {
        showNotice(err.message || 'Subscribe to access this exercise.', 'warning');
        return;
      }
      showNotice(err?.message || 'Could not start question set.', 'danger');
    }
  };
  const handleSubmitMcqAnswer = async (questionId, answer) => {
    if (!activeMcqSession) throw new Error('No active session.');
    const result = await api.submitMcqAnswer(activeMcqSession.id, questionId, answer, token);
    return result;
  };
  const handleSubmitMcqSession = async () => {
    if (!activeMcqSession) throw new Error('No active session.');
    const result = await api.submitMcqSession(activeMcqSession.id, token);
    return result.session;
  };
  const handleRestartMcq = async () => {
    if (!activeMcqSession?.mcqExerciseId) return;
    const result = await api.startMcqExercise(activeMcqSession.mcqExerciseId, token);
    setActiveMcqSession(result.session);
  };

  const handleStartDrill = async (drill, options = {}) => {
    try {
      const result = await api.startDrill(drill.id, token, { assignmentId: options.assignmentId || activeAssignmentId });
      setActiveDrillSession(result.session);
      setCurrentView('student-drill-player');
    } catch (err) {
      if (err?.status === 402) {
        showNotice(err.message || 'Subscribe to access this exercise.', 'warning');
        return;
      }
      showNotice(err?.message || 'Could not start exercise.', 'danger');
    }
  };
  const refreshMyAssignments = useCallback(async () => {
    if (!token || user?.role !== 'student') return;
    try {
      const result = await api.listMyExerciseAssignments(token);
      setMyAssignments(result.assignments || []);
    } catch {
      setMyAssignments([]);
    }
  }, [token, user?.role]);

  // Refresh the student's due-assignments list whenever a student logs
  // in / hydrates so the home page can render the "Due exercises" panel
  // without an extra round-trip. Placed after refreshMyAssignments so
  // it isn't read from the TDZ during the first render.
  useEffect(() => {
    if (user?.role === 'student') refreshMyAssignments();
  }, [user?.role, refreshMyAssignments]);

  const handleStartAssignment = async (assignment) => {
    setActiveAssignmentId(assignment.id);
    const drill = { id: assignment.drillId };
    await handleStartDrill(drill, { assignmentId: assignment.id });
  };
  // Revision-session handlers. We mirror the drill flow so DrillSessionPlayer
  // can be reused with the same onSubmitAnswer / onSubmitSession contract;
  // it just calls revision endpoints instead of drill ones.
  const refreshRevisionBests = useCallback(async () => {
    if (!token || user?.role !== 'student') return;
    try {
      const result = await api.getRevisionBests(token);
      setRevisionBests(result.best || {});
      if (result.lengthRange) setRevisionLengthRange(result.lengthRange);
      setRevisionAvailableCategories(Array.isArray(result.availableCategories) ? result.availableCategories : []);
      setRevisionAvailableDifficulties(Array.isArray(result.availableDifficulties) ? result.availableDifficulties : []);
      setRevisionAvailableQuestionTypes(Array.isArray(result.availableQuestionTypes) ? result.availableQuestionTypes : []);
      setRevisionRecentSessions(Array.isArray(result.recentSessions) ? result.recentSessions : []);
    } catch {
      setRevisionBests({});
      setRevisionAvailableCategories([]);
      setRevisionAvailableDifficulties([]);
    }
  }, [token, user?.role]);

  // Hydrate revision personal bests for the picker once we know we have a
  // student. Defined here, after refreshRevisionBests, so the dependency
  // isn't read from the temporal dead zone on the first render.
  useEffect(() => {
    if (user?.role === 'student') {
      refreshRevisionBests();
      refreshStudentRevisionExercises();
    }
  }, [user?.role, refreshRevisionBests, refreshStudentRevisionExercises]);

  const handleStartRevision = async (length, filters = {}) => {
    try {
      const result = await api.startRevisionSession(length, token, filters);
      setActiveRevisionSession(result.session);
      setCurrentView('student-revision-player');
    } catch (err) {
      showNotice(err?.message || 'Could not start revision.', 'danger');
    }
  };
  const handleSubmitRevisionAnswer = async (questionId, answer) => {
    if (!activeRevisionSession) return null;
    return api.submitRevisionAnswer(activeRevisionSession.id, questionId, answer, token);
  };
  const handleSubmitRevisionSession = async () => {
    if (!activeRevisionSession) return null;
    const result = await api.submitRevisionSession(activeRevisionSession.id, token);
    refreshRevisionBests();
    refreshStudentRevisionExercises();
    return result.session;
  };
  const handleRestartRevision = async () => {
    const length = activeRevisionSession?.length || 20;
    await handleStartRevision(length);
  };
  const handleBackFromRevision = () => {
    setActiveRevisionSession(null);
    setCurrentView('student-home');
  };

  const handleOpenAssignments = async () => {
    if (!isEducatorUser) return;
    await refreshDrillsWorkspace();
    setCurrentView('educator-assignments');
  };
  const handleOpenAssignmentResults = (assignment) => {
    setResultsAssignmentId(assignment.id);
    setCurrentView('educator-assignment-results');
  };
  const handleSubmitDrillAnswer = async (questionId, answer) => {
    if (!activeDrillSession) return null;
    return api.submitDrillAnswer(activeDrillSession.id, questionId, answer, token);
  };
  const handleSubmitDrillSession = async () => {
    if (!activeDrillSession) return null;
    const result = await api.submitDrillSession(activeDrillSession.id, token);
    // Refresh the student's due-assignments board so a freshly completed
    // assignment moves to "Recently completed" without a page reload.
    if (user?.role === 'student') refreshMyAssignments();
    setActiveAssignmentId(null);
    return result.session;
  };
  const handleRestartDrill = async () => {
    if (!activeDrillSession?.drillId) return;
    const drill = studentDrills.find((d) => d.id === activeDrillSession.drillId) || { id: activeDrillSession.drillId };
    await handleStartDrill(drill);
  };

  const handleOpenAdminSettings = async () => {
    if (token && isEducatorAdmin) {
      await refreshUserAccounts(token);
    }
    setCurrentView('educator-admin');
  };

  const handleOpenEducatorStats = () => setCurrentView('educator-stats');
  const handleOpenPlatformAdmin = () => setCurrentView('platform-admin');
  const handleOpenUpgrade = () => setCurrentView('upgrade');

  const refreshReviewPendingCount = useCallback(async () => {
    if (!token || !isEducatorUser) return;
    try {
      const result = await api.getReviewPendingCount(token);
      setReviewPendingCount(result?.pending || 0);
    } catch {
      // Non-critical: count badge just stays at last known value.
    }
  }, [token, isEducatorUser]);

  useEffect(() => { refreshReviewPendingCount(); }, [refreshReviewPendingCount]);

  const handleOpenReviewQueue = async () => {
    await refreshReviewPendingCount();
    setReviewSessionId(null);
    setReviewModuleType(null);
    setCurrentView('educator-review-queue');
  };
  const handleOpenReviewSession = (moduleType, id) => {
    setReviewModuleType(moduleType);
    setReviewSessionId(id);
    setCurrentView('educator-review-detail');
  };
  const handleBackToReviewQueue = () => {
    setReviewSessionId(null);
    setReviewModuleType(null);
    setCurrentView('educator-review-queue');
  };
  const handleOpenFacilitatorStudentPortfolios = () => {
    setCurrentView('facilitator-student-portfolios');
  };
  const handleSessionReviewed = () => { refreshReviewPendingCount(); };
  const handleCreateFacilitatorCase = (returnView = 'educator-home') => {
    const emptyDraft = createDraftCaseStudy();
    setDraft(emptyDraft);
    setSavedDraft(emptyDraft);
    setAnalytics(null);
    setLiveSessionCode('');
    setLiveSessionState(null);
    setLiveResponses({});
    setCaseAuthoringReturnView(returnView);
    setCurrentView('educator-create');
  };

  const handleEditFacilitatorCase = async (caseStudyId) => {
    const response = await api.getCaseStudy(caseStudyId, token);
    const loadedDraft = hydrateDraftFromCaseStudy(response.caseStudy);
    setDraft(loadedDraft);
    setSavedDraft(loadedDraft);
    setAnalytics(null);
    setCaseAuthoringReturnView('educator-library');
    setCurrentView('educator-create');
  };

  const handleShareFacilitatorCase = async (caseStudyId, email, shareType = 'student') => {
    await api.shareCaseStudy(caseStudyId, email, shareType, token);
    await refreshEducatorData(token, caseStudyId);
    const message = shareType === 'editor'
      ? 'Co-editor added.'
      : 'Case study shared with student.';
    showNotice(message);
  };

  const handleRevokeFacilitatorCaseShare = async (caseStudyId, shareId) => {
    await api.revokeCaseStudyShare(caseStudyId, shareId, token);
    await refreshEducatorData(token, caseStudyId);
    showNotice('Share removed.', 'info');
  };

  const handleCreateCaseStudySet = async (data) => {
    await api.createCaseStudySet(data, token);
    await refreshEducatorData(token);
    showNotice('Case study set created.');
  };

  const handleUpdateCaseStudySet = async (setId, data) => {
    await api.updateCaseStudySet(setId, data, token);
    await refreshEducatorData(token);
    showNotice('Case study set updated.');
  };

  const handleDeleteCaseStudySet = async (setId) => {
    await api.deleteCaseStudySet(setId, token);
    await refreshEducatorData(token);
    showNotice('Case study set deleted.', 'info');
  };

  const handleShareCaseStudySet = async (setId, email) => {
    await api.shareCaseStudySet(setId, email, token);
    await refreshEducatorData(token);
    showNotice('Case study set shared with student.');
  };

  const handleDeleteFacilitatorCase = async (caseStudyId) => {
    await api.deleteCaseStudy(caseStudyId, token);
    await refreshEducatorData(token);
    if (draft.id === caseStudyId) {
      const emptyDraft = createDraftCaseStudy();
      setDraft(emptyDraft);
      setSavedDraft(emptyDraft);
      setAnalytics(null);
    }
    showNotice('Case study deleted.', 'info');
  };

  const handleSetFacilitatorCaseStudentAccess = async (caseStudyId, enabled) => {
    const result = await api.setCaseStudyStudentAccess(caseStudyId, enabled, token);
    await refreshEducatorData(token, caseStudyId);
    if (result?.pendingApproval) {
      showNotice('Sent to facilitator-admin for approval before students can access.', 'info');
      return;
    }
    showNotice(enabled ? 'Self-paced student access turned on.' : 'Student access turned off.', enabled ? 'success' : 'info');
  };

  const handleApproveFacilitatorCase = async (caseStudy, body = '') => {
    try {
      await api.approveCaseStudy(caseStudy.id, token, body);
      await refreshEducatorData(token, caseStudy.id);
      showNotice(`Approved "${caseStudy.title}". Students can now access it.`, 'success');
    } catch (err) {
      showNotice(err?.message || 'Failed to approve case.', 'danger');
      throw err;
    }
  };

  const handleRejectFacilitatorCase = async (caseStudy, reason = '') => {
    const trimmed = String(reason || '').trim();
    if (!trimmed) return;
    try {
      await api.rejectCaseStudy(caseStudy.id, trimmed, token);
      await refreshEducatorData(token, caseStudy.id);
      showNotice('Case sent back to draft with your feedback.', 'info');
    } catch (err) {
      showNotice(err?.message || 'Failed to reject case.', 'danger');
      throw err;
    }
  };

  const handleToggleCaseStudyFreeTier = async (caseStudyId, freeTierEligible) => {
    try {
      await api.setCaseStudyFreeTier(caseStudyId, freeTierEligible, token);
      await refreshEducatorData(token, caseStudyId);
      showNotice(
        freeTierEligible
          ? 'Marked as free-tier — non-paying students can now run this case.'
          : 'Marked as paid — only subscribers can run this case.',
        'success'
      );
    } catch (err) {
      showNotice(err?.message || 'Failed to change free-tier flag.', 'danger');
    }
  };

  const handleEndFacilitatorLiveClassroom = async (caseStudyId) => {
    await api.endLiveClassroom(caseStudyId, token);
    if (draft.id === caseStudyId) {
      setLiveSessionCode('');
      setLiveSessionState(null);
      setLiveResponses({});
    }
    await refreshEducatorData(token, caseStudyId);
    showNotice('Live classroom ended.', 'info');
  };

  const handleCopyStudentShareLink = async (caseStudyId) => {
    const shareLink = `${window.location.origin}/?sharedCase=${encodeURIComponent(caseStudyId)}`;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareLink);
        showNotice('Student link copied. Learners will need to sign in before opening it.');
        return;
      }
    } catch (_error) {
      // Fall back to showing the link below.
    }
      showNotice(`Student share link: ${shareLink}`, 'info');
    };

  const handleCopyPresentationLink = async (caseStudyId) => {
    const presentationLink = `${window.location.origin}/?presentCase=${encodeURIComponent(caseStudyId)}`;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(presentationLink);
        showNotice('Presentation link copied. Facilitators will need to sign in before opening it.');
        return;
      }
    } catch (_error) {
      // Fall back to showing the link below.
    }
    showNotice(`Presentation link: ${presentationLink}`, 'info');
  };

  useEffect(() => {
    const sharedCaseId = getPendingSharedCaseId();
    if (!sharedCaseId || user?.role !== 'student' || !token) {
      return;
    }

    let cancelled = false;
    const openSharedCase = async () => {
      try {
        const started = await api.startCaseSession(sharedCaseId, token);
        const loaded = await api.getCaseSession(started.session.id, token);
        if (cancelled) {
          return;
        }
        setActiveCaseSession(loaded.session);
        setGrading(null);
        setCurrentView('player');
        await refreshStudentData(token);
        if (cancelled) {
          return;
        }
        showNotice(
          loaded.session?.attemptNumber ? `Shared case attempt ${loaded.session.attemptNumber} opened.` : 'Shared case session opened.',
          'info'
        );
        if (!cancelled) {
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } catch (error) {
        if (!cancelled) {
          setCurrentView('student-cases');
          showNotice(error.message || 'This shared case study is not currently available.', 'warning');
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }
    };

    openSharedCase();
    return () => {
      cancelled = true;
    };
  }, [token, user, refreshStudentData]);

  const renderMainView = () => {
    // While the initial /api/auth/me call is in flight (page refresh with a
    // valid token in localStorage), don't paint the public landing — show a
    // minimal placeholder so authenticated users never see a flash of the
    // signed-out marketing/login view before hydrate resolves.
    if (hydrating && token) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading…</span>
          </Spinner>
        </div>
      );
    }

    if (currentView === 'educator-home' && isEducatorUser) {
      return (
        <FacilitatorHome
          caseCount={caseStudies.length}
          spotErrorCount={spotErrorWorkspace.length}
          outpatientCount={outpatientWorkspace.length}
          qbiCount={qbiWorkspace.length}
          showAdminTile={isEducatorAdmin}
          onCreateCaseStudy={() => handleCreateFacilitatorCase('educator-home')}
          onOpenCaseLibrary={handleOpenCaseLibrary}
          onOpenQbiLibrary={handleOpenQbiLibrary}
          onOpenAssignments={handleOpenAssignments}
          onOpenSpotErrorLibrary={handleOpenEducatorSpotError}
          onOpenOutpatientLibrary={handleOpenEducatorOutpatient}
          onOpenEpma={handleOpenEpmaWorkspace}
          onOpenReviewQueue={handleOpenReviewQueue}
          reviewPendingCount={reviewPendingCount}
          onOpenStudentPortfolios={handleOpenFacilitatorStudentPortfolios}
          onOpenStats={isEducatorAdmin ? handleOpenEducatorStats : undefined}
          onOpenAdminSettings={handleOpenAdminSettings}
        />
      );
    }

    if (currentView === 'educator-library' && isEducatorUser) {
      return (
        <FacilitatorCaseLibrary
          ownedCaseStudies={caseStudies}
          caseStudySets={caseStudySets}
          onBack={() => setCurrentView('educator-home')}
          onCreateCaseStudy={() => handleCreateFacilitatorCase('educator-library')}
          onEditCaseStudy={handleEditFacilitatorCase}
          onViewResults={handleOpenCaseResults}
          onDeleteCaseStudy={handleDeleteFacilitatorCase}
            onArchiveCaseStudy={handleArchiveCase}
            onUnarchiveCaseStudy={handleUnarchiveCase}
            onScheduleCaseStudyPublish={handleScheduleCasePublish}
            onToggleStudentAccess={handleSetFacilitatorCaseStudentAccess}
            onEndLiveClassroom={handleEndFacilitatorLiveClassroom}
            onTestCaseStudy={handleTestFacilitatorCase}
            onPublishCaseStudy={handlePublishFacilitatorCase}
            onPresentCaseStudy={handleOpenFacilitatorPresentation}
            onCopyPresentationLink={handleCopyPresentationLink}
            onShareCaseStudy={handleShareFacilitatorCase}
            onRevokeCaseStudyShare={handleRevokeFacilitatorCaseShare}
            onCreateCaseStudySet={handleCreateCaseStudySet}
            onUpdateCaseStudySet={handleUpdateCaseStudySet}
            onDeleteCaseStudySet={handleDeleteCaseStudySet}
          onShareCaseStudySet={handleShareCaseStudySet}
          onCopyStudentLink={handleCopyStudentShareLink}
          onRefreshCaseStudies={(caseStudyId) => refreshEducatorData(token, caseStudyId)}
          onToggleFreeTier={handleToggleCaseStudyFreeTier}
          onApproveCase={handleApproveFacilitatorCase}
          onRejectCase={handleRejectFacilitatorCase}
          user={user}
          tenant={tenant}
          token={token}
        />
      );
    }

    if (currentView === 'educator-results' && isEducatorUser) {
      const resultsCase = caseStudies.find((item) => item.id === resultsCaseId) || null;
      return (
        <FacilitatorCaseResults
          caseStudy={resultsCase}
          analytics={analytics}
          onBack={() => setCurrentView('educator-library')}
          onRefresh={handleRefreshAnalytics}
          onOpenAttempt={handleOpenFacilitatorAttempt}
          onResetAttempt={handleResetFacilitatorAttempt}
        />
      );
    }

    if (currentView === 'educator-attempt' && isEducatorUser) {
      const resultsCase = caseStudies.find((item) => item.id === resultsCaseId) || null;
      return (
        <FacilitatorAttemptReview
          caseStudy={resultsCase}
          attempt={selectedAttempt}
          onBack={() => setCurrentView('educator-results')}
          onSaveReview={handleSaveFacilitatorAttemptReview}
          onResetAttempt={handleResetFacilitatorAttempt}
          savingReview={savingAttemptReview}
        />
      );
    }

    if (currentView === 'educator-create' && isEducatorUser) {
      const currentCaseMeta = caseStudies.find((item) => item.id === draft.id) || null;
      return (
        <FacilitatorCaseAuthoringWorkspace
          caseStudy={draft}
          savedCaseStudy={savedDraft}
          caseMeta={currentCaseMeta}
          drugLibrary={drugLibrary}
          commonConditions={commonConditions}
          onChange={setDraft}
          onSave={handleSave}
          isSaving={saveLoading}
          onBack={() => setCurrentView(caseAuthoringReturnView || 'educator-library')}
          onNotice={showNotice}
          onTestDraft={handleTestCurrentDraft}
        />
      );
    }

    if (currentView === 'educator-admin' && isEducatorAdmin) {
      return (
          <FacilitatorAdminWorkspace
            user={user}
            drugLibrary={drugLibrary}
            userAccounts={userAccounts}
            onBack={() => setCurrentView('educator-home')}
            onPreviewDrugLibraryImport={handlePreviewDrugLibraryImport}
            onImportDrugLibrary={handleImportDrugLibrary}
            onAddRoute={handleAddRouteOption}
            onDeleteRoute={handleDeleteRouteOption}
            onUpdateRoute={handleUpdateRouteOption}
            onMoveRoute={handleMoveRouteOption}
            onImportRoutes={handleImportRouteOptions}
            onAddFrequency={handleAddFrequencyOption}
            onDeleteFrequency={handleDeleteFrequencyOption}
            onUpdateFrequency={handleUpdateFrequencyOption}
            onMoveFrequency={handleMoveFrequencyOption}
            onImportFrequencies={handleImportFrequencyOptions}
            onAddIndication={handleAddIndicationOption}
            onDeleteIndication={handleDeleteIndicationOption}
            onUpdateIndication={handleUpdateIndicationOption}
            onImportIndications={handleImportIndicationOptions}
            onAddUnit={handleAddUnitOption}
            onDeleteUnit={handleDeleteUnitOption}
            onUpdateUnit={handleUpdateUnitOption}
            onImportUnits={handleImportUnitOptions}
            onAddForm={handleAddFormOption}
            onDeleteForm={handleDeleteFormOption}
            onUpdateForm={handleUpdateFormOption}
            onImportForms={handleImportFormOptions}
            onAddCriticalMedicine={handleAddCriticalMedicine}
            onDeleteCriticalMedicine={handleDeleteCriticalMedicine}
            onAddControlledDrug={handleAddControlledDrug}
            onDeleteControlledDrug={handleDeleteControlledDrug}
            onAddOrderSet={handleAddOrderSet}
            onUpdateOrderSet={handleUpdateOrderSet}
            onDeleteOrderSet={handleDeleteOrderSet}
            onUpdateDrug={handleUpdateDrugLibraryItem}
            onAddDrug={handleCreateDrugLibraryItem}
            onDeleteDrug={handleDeleteDrugLibraryItem}
            onSuspendUser={handleSuspendUserAccount}
            onRestoreUser={handleRestoreUserAccount}
            onRemoveUserAccess={handleRemoveUserAccountAccess}
            isSaving={saveLoading}
            token={token}
            onTenantSaved={applyTenantUpdate}
          />
        );
      }

    if (currentView === 'platform-admin' && isPlatformAdmin) {
      return (
        <PlatformAdminWorkspace
          token={token}
          onBack={() => setCurrentView('public')}
        />
      );
    }

    if (currentView === 'upgrade') {
      return (
        <UpgradePage token={token} onBack={() => setCurrentView('student-home')} />
      );
    }

    if (currentView === 'educator-present' && isEducatorUser) {
      return (
        <FacilitatorCasePresentation
          liveState={liveSessionState || {
            sessionCode: liveSessionCode,
            payload: draft,
            stepIndex: 0,
            revealAnswers: false,
          }}
          liveResponses={liveResponses}
          onBack={handleOpenCaseLibrary}
          onChangeStep={handleSetLiveStep}
          onToggleReveal={handleSetLiveReveal}
          onRevealQuestion={handleRevealLiveQuestion}
          onChangePresentationStage={handleSetLivePresentationStage}
          onChangeStageIndex={handleSetLiveStageIndex}
          onEndSession={() => handleEndFacilitatorLiveClassroom(draft.id)}
        />
      );
    }

    if (currentView === 'player' && activeCaseSession) {
      return <CaseSessionPlayer session={activeCaseSession} drugLibrary={drugLibrary} currentUser={user} onSave={handleSaveCaseSession} onSubmit={handleSubmitCaseSession} onBack={handleOpenStudentCases} saving={saveLoading} grading={grading} notice={notice} tenantDisplayName={tenant?.displayName || ''} />;
    }

    if (currentView === 'educator-test-player' && activeCaseSession && isEducatorUser) {
      return (
        <CaseSessionPlayer
          session={activeCaseSession}
          drugLibrary={drugLibrary}
          currentUser={user}
          onSave={handleSaveCaseSession}
          onSubmit={handleSubmitCaseSession}
          onBack={() => {
            setActiveCaseSession(null);
            setGrading(null);
            setCurrentView(caseTestReturnView);
          }}
          saving={saveLoading}
          grading={grading}
          notice={notice}
          previewMode
          backLabel={caseTestReturnView === 'educator-create' ? 'Back to editor' : 'Back to case library'}
          tenantDisplayName={tenant?.displayName || ''}
        />
      );
    }

    if (currentView === 'live') {
      return (
        <LiveSessionView
          liveState={liveSessionState || {
            sessionCode: connectedSessionCode,
            payload: studentCaseStudy,
            stepIndex: 0,
            revealAnswers: false,
          }}
          drugLibrary={drugLibrary}
          liveResponses={liveResponses}
          participantId={participantId}
          onSubmitAnswer={handleSubmitLiveResponse}
          onLeave={handleLeaveLive}
        />
      );
    }

    if (currentView === 'student-cases' && user?.role === 'student') {
      return (
        <StudentDashboard
          library={library}
          caseStudySets={studentCaseStudySets}
          sessions={studentSessions}
          liveSessions={studentLiveSessions}
          onStartCase={handleStartCase}
          onResumeSession={handleResumeSession}
          onRejoinLiveSession={handleRejoinLiveSession}
          onToggleFavourite={handleToggleFavouriteCaseStudy}
          onBack={() => setCurrentView('student-home')}
        />
      );
    }

    // Lecturers + educator-admins get the same ePMA sandbox as students
    // so they can admit their own test patients, prescribe and rehearse
    // scenarios from the educator side. The underlying endpoints are
    // already role-agnostic — only the React route was student-gated.
    if (currentView === 'student-epma' && Boolean(user)) {
      return (
        <StudentEpmaWorkspace
          currentUser={user}
          drugLibrary={drugLibrary}
          onBack={() => setCurrentView(isEducatorUser ? 'educator-home' : 'student-home')}
          onBackToFinder={handleOpenEpmaWorkspace}
          onSearch={handleSearchPatient}
          onSelectPatient={handleSelectPatient}
          onCreatePatient={handleCreateTestPatient}
          onUpdateAllergies={handleUpdatePatientAllergies}
          onUpdateCaseNotes={handleUpdatePatientCaseNotes}
          onUpdatePrescriptions={handleUpdatePatientPrescriptions}
          onUpdateMeasurements={handleUpdatePatientMeasurements}
          onDeleteMeasurement={handleDeletePatientMeasurement}
          onDischargePatient={handleDischargePatient}
          onReadmitPatient={handleReadmitPatient}
          onVerifyWitness={handleVerifyWitness}
          onBlockedPrescribe={handleBlockedPrescribe}
          onApprovalToast={handleApprovalToast}
          onUpdateDischargePrescription={handleUpdateDischargePrescription}
          tenantDisplayName={tenant?.displayName || ''}
          searchResults={patientSearchResults}
          recentPatients={recentPatients}
          commonConditions={commonConditions}
          searchState={patientSearchState}
          selectedPatient={selectedPatient}
        />
      );
    }

    if (currentView === 'student-home' && user?.role === 'student') {
      return (
        <StudentHome
          token={token}
          tenantType={tenant?.tenantType}
          onOpenCases={handleOpenStudentCases}
          onOpenMcqs={handleOpenStudentMcqs}
          onOpenEpma={handleOpenEpmaWorkspace}
          onOpenSpotError={handleOpenSpotErrorDrills}
          onOpenOutpatient={handleOpenStudentOutpatient}
          onOpenPortfolio={handleOpenStudentPortfolio}
          sessionInput={sessionInput}
          onSessionInputChange={setSessionInput}
          onJoinLive={handleJoinLive}
          assignments={myAssignments}
          onStartAssignment={handleStartAssignment}
          onStartRevision={handleStartRevision}
          revisionBests={revisionBests}
          revisionLengthRange={revisionLengthRange}
          revisionAvailableCategories={revisionAvailableCategories}
          revisionAvailableDifficulties={revisionAvailableDifficulties}
          revisionAvailableQuestionTypes={revisionAvailableQuestionTypes}
          revisionRecentSessions={revisionRecentSessions}
          curatedRevisionExercises={studentRevisionExercises}
          onStartCuratedRevision={handleStartCuratedRevision}
        />
      );
    }

    if (currentView === 'student-portfolio' && user?.role === 'student') {
      return (
        <StudentPortfolioView
          token={token}
          user={user}
          onBack={() => setCurrentView('student-home')}
        />
      );
    }

    if (currentView === 'student-portfolio-print' && user?.role === 'student') {
      return (
        <StudentPortfolioPrintView
          token={token}
          user={user}
        />
      );
    }

    if (currentView === 'student-spot-error' && user?.role === 'student') {
      return (
        <StudentSpotErrorWorkspace
          token={token}
          onBack={() => setCurrentView('student-home')}
        />
      );
    }

    if (currentView === 'student-outpatient' && user?.role === 'student') {
      return (
        <StudentOutpatientWorkspace
          token={token}
          user={user}
          tenant={tenant}
          onBack={() => setCurrentView('student-home')}
          onUpgrade={handleOpenUpgrade}
        />
      );
    }

    if (currentView === 'student-drill-player' && user?.role === 'student' && activeDrillSession) {
      // Legacy student calc session player — calc questions still play
      // through this surface when started from an Assignment that pointed
      // at a (now folded) drill row. The catalog is gone; new entry
      // points are the student Question bank tile + Mixed revision.
      return (
        <DrillSessionPlayer
          session={activeDrillSession}
          onSubmitAnswer={handleSubmitDrillAnswer}
          onSubmitSession={handleSubmitDrillSession}
          onRestart={handleRestartDrill}
          onBack={() => { setActiveDrillSession(null); setCurrentView('student-home'); }}
        />
      );
    }

    if (currentView === 'student-revision-player' && user?.role === 'student' && activeRevisionSession) {
      return (
        <DrillSessionPlayer
          session={activeRevisionSession}
          onSubmitAnswer={handleSubmitRevisionAnswer}
          onSubmitSession={handleSubmitRevisionSession}
          onRestart={handleRestartRevision}
          onBack={handleBackFromRevision}
        />
      );
    }

    if (currentView === 'educator-assignments' && isEducatorUser) {
      return (
        <FacilitatorAssignments
          token={token}
          drills={drillsWorkspace}
          onOpenResults={handleOpenAssignmentResults}
          onBack={() => setCurrentView('educator-home')}
        />
      );
    }

    if (currentView === 'educator-assignment-results' && isEducatorUser && resultsAssignmentId) {
      return (
        <FacilitatorAssignmentResults
          token={token}
          assignmentId={resultsAssignmentId}
          onBack={() => setCurrentView('educator-assignments')}
        />
      );
    }

    if (currentView === 'student-mcq-catalog' && user?.role === 'student') {
      return (
        <StudentMcqCatalog
          exercises={studentMcqs}
          onStart={handleStartMcq}
          onBack={() => setCurrentView('student-home')}
          initialExerciseId={pendingMcqShareId}
          onInitialExerciseConsumed={() => setPendingMcqShareId(null)}
          tenant={tenant}
        />
      );
    }

    if (currentView === 'student-mcq-player' && user?.role === 'student' && activeMcqSession) {
      return (
        <DrillSessionPlayer
          session={activeMcqSession}
          onSubmitAnswer={handleSubmitMcqAnswer}
          onSubmitSession={handleSubmitMcqSession}
          onRestart={handleRestartMcq}
          onBack={() => { setActiveMcqSession(null); setCurrentView('student-mcq-catalog'); }}
        />
      );
    }

    if (currentView === 'educator-mcq-edit' && isEducatorUser) {
      return (
        <FacilitatorMcqEditor
          initialExercise={editingMcq}
          canCurateFreeTier={isEducatorAdmin || isPlatformAdmin}
          token={token}
          onSave={handleSaveMcq}
          onPublish={handlePublishMcq}
          onArchive={handleArchiveMcq}
          onDelete={handleDeleteMcq}
          onBack={() => { setQbiActiveView('sets'); setCurrentView('educator-qbi-library'); }}
        />
      );
    }

    if (currentView === 'educator-qbi-library' && isEducatorUser) {
      return (
        <FacilitatorQuestionBankItemLibrary
          activeView={qbiActiveView}
          onChangeView={setQbiActiveView}
          items={qbiWorkspace}
          activeGroupKey={qbiActiveGroup}
          onChangeGroup={setQbiActiveGroup}
          onCreate={handleNewQbi}
          onEdit={handleEditQbi}
          onPublish={handlePublishQbi}
          onArchive={handleArchiveQbi}
          onDelete={handleDeleteQbi}
          sets={mcqWorkspace}
          onCreateSet={handleNewMcq}
          onEditSet={handleEditMcq}
          onPublishSet={handlePublishMcq}
          onArchiveSet={handleArchiveMcq}
          onDeleteSet={handleDeleteMcq}
          onBack={() => setCurrentView('educator-home')}
        />
      );
    }

    if (currentView === 'educator-qbi-edit' && isEducatorUser) {
      return (
        <FacilitatorQuestionBankItemEditor
          initialItem={editingQbi}
          defaultType={qbiDefaultType}
          onSave={handleSaveQbi}
          onPublish={handlePublishQbi}
          onArchive={handleArchiveQbi}
          onDelete={handleDeleteQbi}
          onBack={() => setCurrentView('educator-qbi-library')}
        />
      );
    }

    if (currentView === 'educator-spot-error-library' && isEducatorUser) {
      return (
        <FacilitatorSpotErrorLibrary
          drills={spotErrorWorkspace}
          onCreate={handleNewSpotErrorDrill}
          onEdit={handleEditSpotErrorDrill}
          onPublish={handlePublishSpotErrorDrill}
          onArchive={handleArchiveSpotErrorDrill}
          onDelete={handleDeleteSpotErrorDrill}
          onBack={() => setCurrentView('educator-home')}
        />
      );
    }

    if (currentView === 'educator-spot-error-edit' && isEducatorUser) {
      return (
        <FacilitatorSpotErrorEditor
          drill={editingSpotErrorDrill}
          loadFullDrill={handleLoadFullSpotErrorDrill}
          onSave={handleSaveSpotErrorDrill}
          onPreview={handlePreviewSpotErrorDrill}
          onCancel={() => { setEditingSpotErrorDrill(null); setSpotErrorEditorError(''); setCurrentView('educator-spot-error-library'); }}
          saving={spotErrorEditorSaving}
          errorMessage={spotErrorEditorError}
        />
      );
    }

    if (currentView === 'educator-outpatient-library' && isEducatorUser) {
      return (
        <FacilitatorOutpatientLibrary
          drills={outpatientWorkspace}
          user={user}
          tenant={tenant}
          onCreate={handleNewOutpatientDrill}
          onEdit={handleEditOutpatientDrill}
          onPublish={handlePublishOutpatientDrill}
          onArchive={handleArchiveOutpatientDrill}
          onDelete={handleDeleteOutpatientDrill}
          onBack={() => setCurrentView('educator-home')}
        />
      );
    }

    if (currentView === 'educator-outpatient-edit' && isEducatorUser) {
      return (
        <FacilitatorOutpatientEditor
          drill={editingOutpatientDrill}
          loadFullDrill={handleLoadFullOutpatientDrill}
          onSave={handleSaveOutpatientDrill}
          onPreview={handlePreviewOutpatientDrill}
          onCancel={() => { setEditingOutpatientDrill(null); setOutpatientEditorError(''); setCurrentView('educator-outpatient-library'); }}
          saving={outpatientEditorSaving}
          errorMessage={outpatientEditorError}
        />
      );
    }

    if (currentView === 'educator-stats' && isEducatorAdmin) {
      return (
        <EducatorStatsView
          token={token}
          onBack={() => setCurrentView('educator-home')}
        />
      );
    }

    if (currentView === 'educator-review-queue' && isEducatorUser) {
      return (
        <FacilitatorReviewQueue
          token={token}
          onOpenSession={handleOpenReviewSession}
          onBack={() => setCurrentView('educator-home')}
        />
      );
    }

    if (currentView === 'facilitator-student-portfolios' && isEducatorUser) {
      return (
        <FacilitatorStudentPortfolios
          token={token}
          user={user}
          onBack={() => setCurrentView('educator-home')}
        />
      );
    }

    if (currentView === 'educator-review-detail' && isEducatorUser && reviewSessionId && reviewModuleType) {
      return (
        <FacilitatorReviewDetail
          token={token}
          moduleType={reviewModuleType}
          sessionId={reviewSessionId}
          onBack={handleBackToReviewQueue}
          onReviewed={handleSessionReviewed}
        />
      );
    }

    // Defensive: a signed-in user with a stale or unrecognised currentView
    // (e.g. left over from a previous session, or a stray Back button click)
    // should land on their role's home, not the public marketing / login
    // landing. Without this, an authenticated user briefly sees the public
    // landing mid-app, which is confusing and looks like a credentials bug.
    //
    // Important: do NOT intercept 'public' — the user explicitly asked for
    // the public landing (e.g. clicked 'Exit admin'). Intercepting 'public'
    // here makes Exit-style buttons behave like dead clicks because we
    // immediately re-render the workspace they were trying to leave.
    if (token && user && currentView !== 'public') {
      if (user.role === 'platform_admin' && isPlatformAdmin) {
        return (
          <PlatformAdminWorkspace
            token={token}
            onBack={() => setCurrentView('public')}
          />
        );
      }
      if ((user.role === 'educator' || user.role === 'educator_admin') && isEducatorUser) {
        return (
          <FacilitatorHome
            caseCount={caseStudies.length}
            showAdminTile={isEducatorAdmin}
            onCreateCaseStudy={() => handleCreateFacilitatorCase('educator-home')}
            onOpenCaseLibrary={handleOpenCaseLibrary}
            onOpenAdminSettings={handleOpenAdminSettings}
          />
        );
      }
      if (user.role === 'student') {
        return (
          <StudentHome
            token={token}
            tenantType={tenant?.tenantType}
            onOpenCases={handleOpenStudentCases}
            onOpenMcqs={handleOpenStudentMcqs}
            onOpenEpma={handleOpenEpmaWorkspace}
            onOpenSpotError={handleOpenSpotErrorDrills}
            onOpenOutpatient={handleOpenStudentOutpatient}
            onOpenPortfolio={handleOpenStudentPortfolio}
            sessionInput={sessionInput}
            onSessionInputChange={setSessionInput}
            onJoinLive={handleJoinLive}
            assignments={myAssignments}
            onStartAssignment={handleStartAssignment}
            onStartRevision={handleStartRevision}
            revisionBests={revisionBests}
            revisionLengthRange={revisionLengthRange}
            revisionAvailableCategories={revisionAvailableCategories}
            revisionAvailableDifficulties={revisionAvailableDifficulties}
            revisionAvailableQuestionTypes={revisionAvailableQuestionTypes}
            revisionRecentSessions={revisionRecentSessions}
            curatedRevisionExercises={studentRevisionExercises}
            onStartCuratedRevision={handleStartCuratedRevision}
          />
        );
      }
    }

    if (currentView === 'join-register' && inviteContext) {
      return (
        <div className="login-landing">
          <div className="login-landing__hero">
            <Container>
              <Row className="justify-content-center">
                <Col lg={5}>
                  <div className="alert alert-info mb-3 text-center">
                    <strong>{inviteContext.tenantDisplayName || 'Your institution'}</strong> has invited you to join{' '}
                    <strong>{inviteContext.cohortName}</strong>. Create your account below.
                  </div>
                  <div className="login-landing__card">
                    <Login
                      onSubmit={handleAuthSubmit}
                      loading={authLoading}
                      error={authError}
                      onForgotPassword={handleForgotPassword}
                      initialMode="register"
                      onModeChange={setAuthMode}
                    />
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
        </div>
      );
    }

    // Central students tenant gets the marketing landing (free vs paid,
    // sign-up CTA, value prop). University tenants get the simple login
    // card. Their students arrive via institutional comms and don't need
    // selling. Require both tenantType AND a resolved slug so we don't
    // render the marketing landing on the admin host or before the
    // /api/tenant/me response lands.
    if (tenant?.tenantType === 'central_students' && tenant?.slug) {
      return (
        <StudentMarketingLanding
          tenantDisplayName={tenant?.displayName}
          tagline={tenant?.brandLoginTagline}
          onSubmit={handleAuthSubmit}
          authLoading={authLoading}
          authError={authError}
          onForgotPassword={handleForgotPassword}
          authMode={authMode}
          onModeChange={setAuthMode}
        />
      );
    }

    return (
      <div className="login-landing">
        <div className="login-landing__hero">
          <Container>
            <Row className="g-5 align-items-center">
              <Col lg={6} className="login-landing__hero-copy">
                <div className="login-landing__eyebrow">
                  {tenant?.displayName ? tenant.displayName : 'MediCase Teaching Platform'}
                </div>
                <h1 className="login-landing__title">
                  Prescribing practice<br />
                  <span className="login-landing__title-accent">made safer.</span>
                </h1>
                <p className="login-landing__lede">
                  {tenant?.brandLoginTagline
                    || 'Realistic case studies, calculation exercises and live classroom workshops on a hospital-style ePMA simulator. Build prescribing confidence safely, before the patient.'}
                </p>
                <ul className="login-landing__benefits">
                  <li><i className="bi bi-journal-medical" /><span>Branching case studies with EPMA chart prescribing</span></li>
                  <li><i className="bi bi-calculator" /><span>Calculation exercises with auto-generated, fixed-answer and multiple-choice questions</span></li>
                  <li><i className="bi bi-calendar-check" /><span>Cohort assignments with release dates, deadlines and completion tracking</span></li>
                  <li><i className="bi bi-journal-bookmark" /><span>Portfolios, CPD logs and lecturer feedback in one place</span></li>
                </ul>
              </Col>
              <Col lg={6}>
                <div className="login-landing__card">
                  <Login
                    onSubmit={handleAuthSubmit}
                    loading={authLoading}
                    error={authError}
                    onForgotPassword={handleForgotPassword}
                    initialMode={authMode}
                    onModeChange={setAuthMode}
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </div>
        <footer className="login-landing__footer">
          <Container className="d-flex justify-content-between align-items-center flex-wrap gap-2 small text-muted">
            <span><strong>{tenant?.displayName || 'MediCase'}</strong> · prescribing simulator</span>
            <a href="https://auxtechna.co.uk" target="_blank" rel="noopener noreferrer">auxtechna.co.uk</a>
          </Container>
        </footer>
      </div>
    );
  };

  // Public portfolio share viewer is a stand-alone route that bypasses
  // all auth and tenant chrome. Token in the URL is the only access
  // control; the public API endpoint enforces revocation + expiry.
  if (isPortfolioShareRoute()) {
    return <PublicPortfolioShareView />;
  }

  return (
    <>
      {token ? (
        <NavBar
          user={user}
          token={token}
          currentView={currentView}
          onViewChange={setCurrentView}
          onNotificationClick={(notification) => {
            if (!notification) return;
            if (notification.type === 'comment_received' && user?.role === 'student') {
              setCurrentView('student-portfolio');
            } else if (notification.type === 'student_entry_added' && isEducatorUser) {
              setCurrentView('facilitator-student-portfolios');
            }
          }}
          onOpenCaseLibrary={handleOpenCaseLibrary}
          onOpenAdminSettings={handleOpenAdminSettings}
          onOpenPlatformAdmin={handleOpenPlatformAdmin}
          onOpenSubscription={() => setSubscriptionModal(true)}
          onOpenCases={handleOpenStudentCases}
          onOpenEpma={handleOpenEpmaWorkspace}
          onSelectPatient={handleSelectPatient}
          onLoginClick={() => {
            setAuthMode('login');
            setLoginModal(true);
          }}
          onCreateAccountClick={() => {
            setAuthMode('register');
            setLoginModal(true);
          }}
          onForgotPassword={handleForgotPassword}
          onLogout={handleLogout}
          onUpdateDiscipline={handleUpdateDiscipline}
          activeSessionCode={liveSessionCode}
          sessionInput={sessionInput}
          onSessionInputChange={setSessionInput}
          onJoinLive={handleJoinLive}
          studentError={studentError}
          recentPatients={recentPatients}
          recentPatientAccesses={recentPatientAccesses}
        />
      ) : null}
      <div className={`app-page-shell ${token ? 'app-page-shell--authed' : ''}`}>
        {renderMainView()}
        {!(currentView === 'player' || (currentView === 'student-epma' && selectedPatient)) ? (
          <AppFooter onOpenPrivacyPolicy={() => setShowPrivacyPolicy(true)} />
        ) : null}
      </div>
      {outpatientPreview && isEducatorUser ? (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1080,
            backgroundColor: '#fff', overflowY: 'auto',
          }}
        >
          <StudentOutpatientWorkspace
            token={token}
            previewMode
            previewPayload={outpatientPreview.payload}
            previewTitle={outpatientPreview.title}
            onExitPreview={handleExitOutpatientPreview}
          />
        </div>
      ) : null}
      {spotErrorPreview && isEducatorUser ? (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1080,
            backgroundColor: '#fff', overflowY: 'auto',
          }}
        >
          <StudentSpotErrorWorkspace
            token={token}
            previewMode
            previewPayload={spotErrorPreview.payload}
            previewTitle={spotErrorPreview.title}
            onExitPreview={handleExitSpotErrorPreview}
          />
        </div>
      ) : null}
      <PrivacyPolicyModal show={showPrivacyPolicy} onHide={() => setShowPrivacyPolicy(false)} />
      <SubscriptionSettings
        user={user}
        token={token}
        show={subscriptionModal}
        onHide={() => setSubscriptionModal(false)}
        onUpgrade={() => { setSubscriptionModal(false); handleOpenUpgrade(); }}
      />
      <ToastContainer position="top-end" className="p-3 mt-5 app-toast-container">
        <Toast show={Boolean(notice)} onClose={() => setNotice(null)} delay={4000} autohide bg={notice?.variant || 'success'}>
          <Toast.Body className="text-white fw-semibold">{notice?.message}</Toast.Body>
          
        </Toast>
      </ToastContainer>
      <Modal show={loginModal} onHide={() => { if (token) { setLoginModal(false); } }} backdrop={token ? true : 'static'} keyboard={Boolean(token)}>
        <Modal.Header closeButton={Boolean(token)}>
          <Modal.Title>{authMode === 'login' ? 'Sign in to the teaching platform' : 'Create your teaching platform account'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Login
            onSubmit={handleAuthSubmit}
            loading={authLoading}
            error={authError}
            onForgotPassword={handleForgotPassword}
            initialMode={authMode}
            onModeChange={setAuthMode}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default App;
